import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Collapse from '@mui/material/Collapse';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CloseIcon from '@mui/icons-material/Close';

import VideoCreativeService from 'services/VideoCreativeService';
import AdDetailDialog from './AdDetailDialog';
import useSortableTable from 'shared/useSortableTable';
import { MODEL_OPTIONS, DEFAULT_GENERATION_MODEL } from './models';
import {
  Product, ProductBrief, PatternRecommendation, PatternDistribution,
  StoryboardConfig, ScrapedAd,
  CIAd, CIAdDetail, EnumValue,
  MarketingBrief, MarketingBriefSection, TemplateElementKey,
  TemplatePatternKey, FeatureSummaryKey,
  TemplatePatternMap, TemplatePatternEntry,
  MarketingBriefSelection, AudienceSelection, AngleSelection,
  AngleFieldKey,
  ProductPromotion,
} from './types';

// Pattern keys → CI_ENUM_VALUES enum_type (snake_case). Used to populate
// the per-pattern dropdowns in custom mode.
const PATTERN_ENUM_TYPES: Record<TemplatePatternKey, string> = {
  hookDevice: 'hook_device',
  persuasionAngle: 'persuasion_angle',
  primaryEmotion: 'primary_emotion',
  tone: 'tone',
  narrativeStructure: 'narrative_structure',
  proofType: 'proof_type',
  productionFormat: 'production_format',
};

const PATTERN_KEY_LABELS: Record<TemplatePatternKey, string> = {
  hookDevice: 'Hook device',
  persuasionAngle: 'Persuasion angle',
  primaryEmotion: 'Emotion',
  tone: 'Tone',
  narrativeStructure: 'Narrative structure',
  proofType: 'Proof type',
  productionFormat: 'Production format',
};

const ALL_PATTERN_KEYS: TemplatePatternKey[] = [
  'hookDevice', 'persuasionAngle', 'primaryEmotion', 'tone',
  'narrativeStructure', 'proofType', 'productionFormat',
];

const FEATURE_SUMMARY_KEYS: Array<{ key: FeatureSummaryKey; label: string }> = [
  { key: 'hookSummary',            label: 'Hook summary' },
  { key: 'problemSummary',         label: 'Problem summary' },
  { key: 'mechanismSummary',       label: 'Mechanism summary' },
  { key: 'solutionSummary',        label: 'Solution summary' },
  { key: 'coreBenefit',            label: 'Core benefit' },
  { key: 'uniqueAngle',            label: 'Unique angle' },
  { key: 'strongestMomentSummary', label: 'Strongest moment' },
];

// All seven summary keys, for callers (e.g. the batch dialog) that mirror
// the deep path's "send everything" default without the labels.
export const ALL_FEATURE_SUMMARY_KEYS: FeatureSummaryKey[] = FEATURE_SUMMARY_KEYS.map(({ key }) => key);

const ANGLE_FIELD_KEYS: Array<{ key: AngleFieldKey; label: string }> = [
  { key: 'angle',              label: 'Angle' },
  { key: 'customerPain',       label: 'Customer pain' },
  { key: 'corePromise',        label: 'Core promise' },
  { key: 'supportingEvidence', label: 'Supporting evidence' },
  { key: 'suggestedHook',      label: 'Suggested hook' },
  { key: 'whyItWorks',         label: 'Why it works' },
];

// Build the default custom-mode selections for a freshly-added template ad.
// Patterns: every pattern enabled, prefilled from the ad's features (falls
// back to `value: null` = "inherit from ad" when a feature is missing).
// Accepts either the nested CIAdDetail shape (features under .features) or
// a flattened list row (ad.hookDevice etc.), so the batch dialog can share it.
// Feature summaries: every key with a non-empty stored value is enabled.
// Segments: all of the ad's segments are enabled.
export function defaultPatternsForAd(ad: CIAdDetail | ScrapedAd | undefined): TemplatePatternMap {
  const source = (ad && 'features' in ad && ad.features
    ? ad.features
    : ad) as Record<string, unknown> | undefined;
  const map: TemplatePatternMap = {};
  for (const key of ALL_PATTERN_KEYS) {
    const raw = source?.[key];
    map[key] = { enabled: true, value: typeof raw === 'string' && raw.trim() !== '' ? raw : null };
  }
  return map;
}

function defaultFeatureSummariesForAd(ad: CIAdDetail | undefined): FeatureSummaryKey[] {
  if (!ad?.features) return [];
  const f = ad.features as unknown as Record<string, unknown>;
  return FEATURE_SUMMARY_KEYS
    .filter(({ key }) => typeof f[key] === 'string' && (f[key] as string).trim().length > 0)
    .map(({ key }) => key);
}

function defaultSegmentsForAd(ad: CIAdDetail | undefined): string[] {
  return (ad?.segments || []).map(s => s.segmentId);
}

// Total length of a reference ad in seconds, used to seed the
// generation target-duration so the new script comes out roughly the
// same length as the original. We use max(endTime) across segments
// rather than summing durationSec, which handles gaps/overlaps
// gracefully and matches the perceived clip length.
function totalDurationForAd(ad: CIAdDetail | undefined): number | null {
  const segs = ad?.segments || [];
  if (segs.length === 0) return null;
  let max = 0;
  for (const s of segs) {
    if (typeof s.endTime === 'number' && s.endTime > max) max = s.endTime;
  }
  if (!max) return null;
  return Math.max(5, Math.min(180, Math.round(max)));
}

// Initialize an all-on marketing brief selection from a freshly-loaded
// brief. Every scalar field defaults true; every array field defaults to
// all-indexes selected.
function defaultMarketingBriefSelection(brief: MarketingBrief | null): MarketingBriefSelection {
  if (!brief) return {};
  const pos = brief.positioning || {};
  const ben = brief.benefits || {};
  const fc = brief.featuresAndClaims || {};
  const proof = brief.proof || {};
  const range = (arr: unknown[] | undefined) => (Array.isArray(arr) ? arr.map((_, i) => i) : []);
  return {
    positioning: {
      corePromise: !!pos.corePromise,
      mainAngle: !!pos.mainAngle,
      differentiators: range(pos.differentiators),
    },
    benefits: { primary: range(ben.primary) },
    featuresAndClaims: {
      performanceClaims: range(fc.performanceClaims),
      certifications: range(fc.certifications),
      proprietaryTech: range(fc.proprietaryTech),
    },
    proof: {
      rating: proof.rating != null,
      reviewCount: proof.reviewCount != null,
      reviewThemes: range(proof.reviewThemes),
      guarantee: !!proof.guarantee,
    },
  };
}

function defaultAudienceSelection(brief: MarketingBrief | null): AudienceSelection {
  const aud = brief?.audience || {};
  const range = (arr: unknown[] | undefined) => (Array.isArray(arr) ? arr.map((_, i) => i) : []);
  return {
    primary: !!aud.primary,
    painPoints: range(aud.painPoints),
    desires: range(aud.desires),
    objections: range(aud.objections),
  };
}

// Pattern-distribution coloring is inlined onto the matched-ads table as
// the value's TEXT color (no pill): dominant patterns (>40%) read in
// green, common (>20%) in blue, rarer values in medium gray. The
// breakdown tooltip uses the same palette for its category dots.
function patternColorForValue(
  distributions: PatternDistribution[] | undefined,
  value: string | null | undefined,
): string | null {
  if (!value || !Array.isArray(distributions)) return null;
  const hit = distributions.find(d => d.value === value);
  if (!hit) return null;
  if (hit.pct > 40) return '#2e7d32';
  if (hit.pct > 20) return '#1976d2';
  return '#757575';
}

function patternBreakdownTooltip(
  field: string,
  distributions: PatternDistribution[] | undefined,
): React.ReactNode {
  if (!Array.isArray(distributions) || distributions.length === 0) return '';
  // MUI's default Tooltip uses a dark background, so we keep the body
  // text white. Only the per-row indicator dots carry the green/blue/gray
  // signal (mirroring the cell text color in the table).
  return (
    <Box sx={{ p: 0.5, color: '#fff' }}>
      <Typography variant="caption" fontWeight={700}
        sx={{ display: 'block', mb: 0.5, color: '#fff' }}>
        {PATTERN_FIELD_LABELS[field] || field}
      </Typography>
      {distributions.slice(0, 10).map(d => (
        <Box key={d.value} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
          <Box sx={{
            width: 6, height: 6, borderRadius: '50%',
            bgcolor: d.pct > 40 ? '#2e7d32' : d.pct > 20 ? '#1976d2' : '#bdbdbd',
            flexShrink: 0,
          }} />
          <Typography variant="caption" sx={{ color: '#fff' }}>
            {formatEnumLabel(d.value)}: {d.pct}%
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function defaultAngleSelection(_brief: MarketingBrief | null): AngleSelection {
  // Start blank — no angle preselected. The user picks one explicitly from
  // the dropdown in the Creative angle subsection.
  return { idx: null, fields: [] };
}

const ALL_MARKETING_BRIEF_SECTIONS: MarketingBriefSection[] =
  ['positioning', 'audience', 'benefits', 'featuresAndClaims', 'proof'];

const MARKETING_BRIEF_SECTION_LABELS: Record<MarketingBriefSection, string> = {
  positioning: 'Positioning',
  audience: 'Audience',
  benefits: 'Benefits',
  featuresAndClaims: 'Features & claims',
  proof: 'Proof',
};

/**
 * Row shape used by the Most Relevant Competitor Ads table after we merge
 * pattern-recommended ads with arbitrary user-added ads. `source` controls how
 * the Relevance column renders; `score` is null for user-added rows.
 */
type CombinedAdRow = {
  adId: string;
  pageName: string | null;
  headline: string | null;
  overallGrade: number | null;
  score: number | null;
  source: 'recommended' | 'added';
  hookDevice: string | null;
  persuasionAngle: string | null;
  primaryEmotion: string | null;
  proofType: string | null;
  startDate: string | null;
};

const service = new VideoCreativeService();

const PATTERN_FIELD_LABELS: Record<string, string> = {
  awarenessLevel: 'Awareness',
  hookDevice: 'Hook Device',
  persuasionAngle: 'Persuasion Angle',
  proofType: 'Proof Type',
  narrativeStructure: 'Narrative Structure',
  primaryEmotion: 'Emotion',
  presenter: 'Presenter',
  productionFormat: 'Production Format',
  tone: 'Tone',
  offerType: 'Offer Type',
  ctaType: 'CTA Type',
};

const ROLE_COLORS: Record<string, string> = {
  hook: '#e65100', problem: '#c62828', mechanism: '#4527a0', solution: '#2e7d32',
  demo: '#00838f', proof: '#1565c0', offer: '#f9a825', cta: '#ad1457',
  bridge: '#546e7a', testimonial: '#6a1b9a', comparison: '#00695c', founder_intro: '#4e342e',
};

function formatEnumLabel(val: string | null) {
  if (!val) return '-';
  return val.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const DEFAULT_TEMPLATE_ELEMENTS: TemplateElementKey[] = ['hook', 'tone', 'pacing', 'cta'];

/**
 * Slice the verbatim spoken script out of a fetched transcript using a
 * segment's (1-indexed) line range. Returns the joined text or null if the
 * range isn't usable.
 */
function scriptLinesForSegment(
  transcript: string | null | undefined,
  lineStart: number | null,
  lineEnd: number | null,
): string | null {
  if (!transcript || lineStart == null) return null;
  const lines = transcript.split(/\r?\n/);
  const start = Math.max(1, lineStart);
  const end = lineEnd != null ? Math.max(start, lineEnd) : start;
  // Transcript lines are 1-indexed in the DB; slice is 0-indexed.
  return lines.slice(start - 1, end).join('\n').trim() || null;
}


export default function ProductMapping() {
  // Product & brief state
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | ''>('');
  const [brief, setBrief] = useState<ProductBrief | null>(null);
  const [briefText, setBriefText] = useState('');
  const [briefLoading, setBriefLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [patterns, setPatterns] = useState<PatternRecommendation | null>(null);
  const [patternsLoading, setPatternsLoading] = useState(false);
  // Set when a pattern-recommendations fetch fails, so the failure is
  // surfaced (with a Retry) instead of the table silently never appearing.
  const [patternsError, setPatternsError] = useState<string | null>(null);

  // Storyboard-generation knobs (the only ones that survived the ensemble
  // mode removal — duration + constraints feed into the template-mode call).
  const [constraints, setConstraints] = useState('');
  const [targetDuration, setTargetDuration] = useState(30);
  // True once the user manually edits the duration field; from then on,
  // toggling a template no longer overwrites their choice with the ad's
  // length. Reset when the product changes. A ref so the async template
  // toggle always sees the current value.
  const durationTouchedRef = useRef(false);
  const [generationModel, setGenerationModel] = useState(DEFAULT_GENERATION_MODEL);

  // Section collapse state. Marketing brief auto-collapses once patterns load
  // (the user shifts focus to the pattern grid + matched-ads table), but stays
  // toggleable. The other panels start expanded and the user controls them.
  const [briefCollapsed, setBriefCollapsed] = useState(false);
  const [patternsCollapsed, setPatternsCollapsed] = useState(false);
  const [matchedAdsCollapsed, setMatchedAdsCollapsed] = useState(false);

  // Ad search / extension. addedAds are arbitrary ads the user pulls in via
  // filters or by ad-id lookup — they render alongside the pattern-recommended
  // ads in the same table, distinguished by a Source chip.
  const [addedAds, setAddedAds] = useState<Record<string, CIAd>>({});
  const [searchOpen, setSearchOpen] = useState(false);
  const [adIdInput, setAdIdInput] = useState('');
  const [adIdLookupError, setAdIdLookupError] = useState<string | null>(null);
  const [adIdLookupLoading, setAdIdLookupLoading] = useState(false);
  const [searchHookDevice, setSearchHookDevice] = useState('');
  const [searchEmotion, setSearchEmotion] = useState('');
  const [searchTone, setSearchTone] = useState('');
  const [searchPersuasionAngle, setSearchPersuasionAngle] = useState('');
  const [searchResults, setSearchResults] = useState<CIAd[]>([]);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchOffset, setSearchOffset] = useState(0);
  const [searchLoading, setSearchLoading] = useState(false);
  const [tableFilterText, setTableFilterText] = useState('');
  const [selectedAdId, setSelectedAdId] = useState<string | null>(null);
  const SEARCH_PAGE_SIZE = 20;

  // Initial product list load (drives the spinner on the Autocomplete).
  const [productsLoading, setProductsLoading] = useState(true);

  // Coordinates the "everything appears together" UX once a product is
  // selected — we keep this true until the brief AND the auto-fired
  // pattern-recommendations fetch both resolve, so the main content area
  // shows a single loading state instead of staggering each panel in.
  const [initializing, setInitializing] = useState(false);

  // A ref so async fetches in the product-select effect can detect when the
  // user has switched products mid-flight and abandon their writes to state.
  const currentProductRef = useRef<number | ''>('');

  // Filters for re-running pattern recommendations. These are intentionally
  // ignored on the first auto-fired query (the initial search runs across all
  // ads, per product spec) and only get passed once the user clicks Re-run.
  const [patternFiltersOpen, setPatternFiltersOpen] = useState(false);
  const [patternFilterStartFrom, setPatternFilterStartFrom] = useState('');
  const [patternFilterStartTo, setPatternFilterStartTo] = useState('');
  const [patternFilterMinGrade, setPatternFilterMinGrade] = useState('');

  // Combined feed for the table — pattern matches first (so the
  // most-relevant ads stay anchored at the top under a `score`-desc sort),
  // then user-added ads. Dedup by adId; recommended wins.
  const combinedAds: CombinedAdRow[] = useMemo(() => {
    const recommended: CombinedAdRow[] = (patterns?.matchedAds ?? []).map(a => ({
      adId: a.adId,
      pageName: a.pageName,
      headline: a.headline,
      overallGrade: a.overallGrade,
      score: a.score,
      source: 'recommended',
      hookDevice: a.hookDevice,
      persuasionAngle: a.persuasionAngle,
      primaryEmotion: a.primaryEmotion,
      proofType: a.proofType,
      startDate: a.startDate ?? null,
    }));
    const recommendedIds = new Set(recommended.map(a => a.adId));
    const added: CombinedAdRow[] = Object.values(addedAds)
      .filter(a => !recommendedIds.has(a.adId))
      .map(a => ({
        adId: a.adId,
        pageName: a.pageName,
        headline: a.headline,
        overallGrade: a.overallGrade,
        score: null,
        source: 'added',
        hookDevice: a.hookDevice ?? null,
        persuasionAngle: a.persuasionAngle ?? null,
        primaryEmotion: a.primaryEmotion ?? null,
        proofType: a.proofType ?? null,
        startDate: a.startDate ?? null,
      }));
    return [...recommended, ...added];
  }, [patterns, addedAds]);

  // Client-side free-text filter over the merged feed (adId / pageName /
  // headline). Cheap; keeps "search the visible list" snappy without a
  // round-trip.
  const filteredCombinedAds: CombinedAdRow[] = useMemo(() => {
    const q = tableFilterText.trim().toLowerCase();
    if (!q) return combinedAds;
    return combinedAds.filter(a =>
      a.adId.toLowerCase().includes(q) ||
      (a.pageName || '').toLowerCase().includes(q) ||
      (a.headline || '').toLowerCase().includes(q)
    );
  }, [combinedAds, tableFilterText]);

  const { sorted: sortedMatchedAds, sortField: matchedSortField, sortDir: matchedSortDir, handleSort: handleMatchedSort } = useSortableTable<CombinedAdRow>(filteredCombinedAds, 'score' as never, 'desc');

  // Pagination for the matched-ads table — 8 rows per page. Reset to page
  // 0 whenever the underlying row set or sort changes so we don't end up
  // on an empty page after filtering.
  const MATCHED_PAGE_SIZE = 8;
  const [matchedPage, setMatchedPage] = useState(0);
  useEffect(() => { setMatchedPage(0); }, [filteredCombinedAds.length, matchedSortField, matchedSortDir]);
  const matchedPageCount = Math.max(1, Math.ceil(sortedMatchedAds.length / MATCHED_PAGE_SIZE));
  const matchedPageStart = matchedPage * MATCHED_PAGE_SIZE;
  const pagedMatchedAds = sortedMatchedAds.slice(matchedPageStart, matchedPageStart + MATCHED_PAGE_SIZE);

  // Generation-in-flight flag (generation itself is async server-side; the
  // result lands in the Scripts tab).
  const [generating, setGenerating] = useState(false);

  // Template-mode state
  const [marketingBrief, setMarketingBrief] = useState<MarketingBrief | null>(null);
  const [marketingBriefMeta, setMarketingBriefMeta] = useState<{ version?: number | null; editSource?: string | null; updatedAt?: string | null }>({});
  const [marketingBriefLoading, setMarketingBriefLoading] = useState(false);
  const [savingBrief, setSavingBrief] = useState(false);
  const [templateAds, setTemplateAds] = useState<Record<string, CIAdDetail>>({});
  const [templateElementMap, setTemplateElementMap] = useState<Record<string, TemplateElementKey[]>>({});
  // Per-(adId, element) text the user has edited in the "What will be
  // mimicked" panel. Only non-empty strings get sent to the backend — empty
  // / missing entries fall back to the auto-extracted segment script.
  const [templateElementOverrides, setTemplateElementOverrides] =
    useState<Record<string, Partial<Record<TemplateElementKey, string>>>>({});
  // Verbatim transcript text per template ad. Fetched from the ad's
  // transcriptUrl when the ad is added as a template; used to slice actual
  // spoken script per segment via lineStart/lineEnd.
  const [templateTranscripts, setTemplateTranscripts] = useState<Record<string, string>>({});
  // null = no creative angle selected. Optional — when null, the
  // generation prompt doesn't get any angle steering; when set, the angle
  // text is appended to the constraints field (treated as a hint, not a
  // mandatory directive).
  const [selectedAngleIdx, setSelectedAngleIdx] = useState<number | null>(null);
  // Both outputs are always produced (single Claude pass derives both),
  // so this is fixed rather than user-toggleable.
  const outputFormats = useMemo<Array<'storyboard' | 'flatScript'>>(() => ['storyboard', 'flatScript'], []);
  const [briefEditorExpanded, setBriefEditorExpanded] = useState(false);

  // What gets passed to the LLM. Surfaced as toggles in TemplatePreview so
  // the user has full control over the prompt inputs. Defaults send
  // everything; user can prune sections to keep the prompt focused.
  const [includeProductBriefInPrompt, setIncludeProductBriefInPrompt] = useState(true);
  const [includedBriefSections, setIncludedBriefSections] =
    useState<MarketingBriefSection[]>(ALL_MARKETING_BRIEF_SECTIONS);

  // adId currently being toggled ON as a template. The toggle is async
  // (fetches the full ad detail + transcript) and visibly slow, so we show
  // a spinner next to a disabled switch in the table while it's in flight.
  const [togglingTemplateAdId, setTogglingTemplateAdId] = useState<string | null>(null);

  // ── Custom-mode granular state ──
  // Enum values grouped by enum_type — used to populate the pattern
  // dropdowns. Loaded once on mount via listEnums().
  const [enumsByType, setEnumsByType] = useState<Record<string, EnumValue[]>>({});
  // Template-usage map { adId: { count, lastUsedAt } }. Powers the
  // "Used" column + the dimmed-row effect in the matched-ads table.
  // Refreshed after every successful generation.
  const [templateUsage, setTemplateUsage] = useState<Record<string, { count: number; lastUsedAt: string | null }>>({});
  // Active product-level promotions, loaded when a product is selected
  // and refreshable on demand. Refreshed daily by the bin/run
  // creative-intelligence/promotions/refreshCli.js cron, but the user
  // can force a re-fetch from the UI.
  const [promotions, setPromotions] = useState<ProductPromotion[]>([]);
  const [promotionsLoading, setPromotionsLoading] = useState(false);
  const [promotionsRefreshing, setPromotionsRefreshing] = useState(false);
  const [promotionsError, setPromotionsError] = useState<string | null>(null);
  // Per-template-ad selections. Initialized when an ad is toggled on; cleared
  // when it's removed or the product changes.
  const [templatePatterns, setTemplatePatterns] =
    useState<Record<string, TemplatePatternMap>>({});
  const [templateFeatureSummaries, setTemplateFeatureSummaries] =
    useState<Record<string, FeatureSummaryKey[]>>({});
  const [templateSegments, setTemplateSegments] =
    useState<Record<string, string[]>>({});
  // Product-level brief / audience / angle selections (single set, not per
  // template, since they describe the target product).
  const [marketingBriefSelection, setMarketingBriefSelection] =
    useState<MarketingBriefSelection>({});
  const [audienceSelection, setAudienceSelection] =
    useState<AudienceSelection>({});
  const [angleSelection, setAngleSelection] =
    useState<AngleSelection>({ idx: null, fields: [] });

  const templateAdIds = useMemo(() => Object.keys(templateAds), [templateAds]);

  useEffect(() => {
    (async () => {
      setProductsLoading(true);
      const result = await service.listProducts(undefined, { hasMarketingBrief: true });
      if (result?.products) setProducts(result.products);
      setProductsLoading(false);
    })();
  }, []);

  // One-shot enum load — populates the pattern dropdowns. Grouped by
  // enum_type so the dropdown for `hookDevice` can ask for `hook_device`.
  useEffect(() => {
    (async () => {
      const result = await service.listEnums();
      if (!result?.enums) return;
      const grouped: Record<string, EnumValue[]> = {};
      for (const e of result.enums) {
        if (!grouped[e.enumType]) grouped[e.enumType] = [];
        grouped[e.enumType].push(e);
      }
      for (const k of Object.keys(grouped)) {
        grouped[k].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
      }
      setEnumsByType(grouped);
    })();
  }, []);

  // Load active promotions when a product is selected. Cleared when
  // the user switches products. The refresh handler hits the
  // force-refresh route and replaces the cache.
  useEffect(() => {
    if (!selectedProductId) {
      setPromotions([]);
      setPromotionsError(null);
      return;
    }
    const targetProductId = selectedProductId;
    setPromotionsLoading(true);
    setPromotionsError(null);
    (async () => {
      const result = await service.getProductPromotions(targetProductId);
      // Bail if the user moved on while this was in flight.
      if (currentProductRef.current !== targetProductId) return;
      if (result?.promotions) setPromotions(result.promotions);
      else setPromotions([]);
      setPromotionsLoading(false);
    })();
  }, [selectedProductId]);

  const handleRefreshPromotions = async () => {
    if (!selectedProductId) return;
    setPromotionsRefreshing(true);
    setPromotionsError(null);
    try {
      const result = await service.refreshProductPromotions(selectedProductId);
      if (result?.promotions) setPromotions(result.promotions);
    } catch (err) {
      setPromotionsError(err instanceof Error ? err.message : String(err));
    } finally {
      setPromotionsRefreshing(false);
    }
  };

  // Template-usage map: ad_id → { count, lastUsedAt }. Loaded once on
  // mount and re-fetched after every generation so the freshly-used
  // template ad immediately reflects in the table.
  const loadTemplateUsage = useCallback(async () => {
    const result = await service.getTemplateAdUsage();
    if (!result?.usage) return;
    const map: Record<string, { count: number; lastUsedAt: string | null }> = {};
    for (const row of result.usage) {
      map[row.adId] = { count: row.usageCount, lastUsedAt: row.lastUsedAt };
    }
    setTemplateUsage(map);
  }, []);
  useEffect(() => { loadTemplateUsage(); }, [loadTemplateUsage]);

  useEffect(() => {
    if (!selectedProductId) {
      setBrief(null); setBriefText(''); setPatterns(null); setPatternsError(null);
      setMarketingBrief(null); setTemplateAds({}); setTemplateElementMap({}); setTemplateElementOverrides({}); setTemplateTranscripts({});
      setTemplatePatterns({}); setTemplateFeatureSummaries({}); setTemplateSegments({});
      setMarketingBriefSelection({}); setAudienceSelection({}); setAngleSelection({ idx: null, fields: [] });
      setSelectedAngleIdx(null);
      setBriefEditorExpanded(false);
      setInitializing(false);
      setBriefCollapsed(false);
      setPatternFiltersOpen(false);
      setPatternFilterStartFrom(''); setPatternFilterStartTo(''); setPatternFilterMinGrade('');
      durationTouchedRef.current = false;
      return;
    }
    currentProductRef.current = selectedProductId;
    durationTouchedRef.current = false;
    const myProductId = selectedProductId;
    (async () => {
      setInitializing(true);
      setBriefLoading(true);
      setMarketingBriefLoading(true);
      setPatternsLoading(true);
      setPatterns(null);
      setPatternsError(null);

      // Brief + marketing brief in parallel.
      const [briefResult, mbResult] = await Promise.all([
        service.getProductBrief(myProductId),
        service.getMarketingBrief(myProductId),
      ]);
      if (currentProductRef.current !== myProductId) return; // user switched products mid-flight

      if (briefResult?.brief) {
        setBrief(briefResult.brief);
        setBriefText(briefResult.brief.briefText);
      } else {
        setBrief(null);
        const product = products.find(p => p.id === myProductId);
        if (product) {
          const parts = [];
          if (product.title) parts.push(`Product: ${product.title}`);
          if (product.category || product.productType) parts.push(`Category: ${product.category || product.productType}`);
          if (product.tags) parts.push(`Tags: ${product.tags}`);
          setBriefText(parts.join('\n'));
        } else { setBriefText(''); }
      }
      const mb = mbResult?.brief || null;
      setMarketingBrief(mb);
      setMarketingBriefMeta({
        version: mbResult?.version ?? null,
        editSource: mbResult?.editSource ?? null,
        updatedAt: mbResult?.updatedAt ?? null,
      });
      // Seed the custom-mode granular brief/audience/angle selections from
      // the freshly-loaded brief. Defaults to "everything selected" so the
      // first generation acts like the old all-sections-on default.
      setMarketingBriefSelection(defaultMarketingBriefSelection(mb));
      setAudienceSelection(defaultAudienceSelection(mb));
      setAngleSelection(defaultAngleSelection(mb));
      setMarketingBriefLoading(false);
      // Reset template selection whenever the product changes.
      setTemplateAds({}); setTemplateElementMap({}); setTemplateElementOverrides({}); setTemplateTranscripts({});
      setTemplatePatterns({}); setTemplateFeatureSummaries({}); setTemplateSegments({});
      setSelectedAngleIdx(null);
      setBriefEditorExpanded(false);
      setBriefLoading(false);

      // Pattern recommendations needs a CI_PRODUCT_BRIEFS row for similarity
      // scoring. Auto-seed one from product metadata if it's missing so the
      // user never has to click "Save brief" to get patterns. (Same fallback
      // that the old click-driven handler had — preserved here.)
      if (!briefResult?.brief) {
        const product = products.find(p => p.id === myProductId);
        await service.updateProductBrief(myProductId, undefined, product?.store);
        if (currentProductRef.current !== myProductId) return;
        const refresh = await service.getProductBrief(myProductId);
        if (currentProductRef.current !== myProductId) return;
        if (refresh?.brief) setBrief(refresh.brief);
      }

      // First query is intentionally unfiltered ("across all ads") — the
      // filters input on top of the table only applies to subsequent
      // re-queries triggered via the Re-run button. Cap the result at the
      // top 10 most relevant ads; the table paginates 8 per page.
      const patternResult = await service.getPatternRecommendations(myProductId, { limit: 10 });
      if (currentProductRef.current !== myProductId) return;
      if (patternResult) setPatterns(patternResult);
      else setPatternsError('Failed to load pattern recommendations.');
      setPatternsLoading(false);
      // Once patterns are in, fold the long brief panel so the table is the
      // focal point. The user can re-expand from the brief's header chevron.
      setBriefCollapsed(true);
      setInitializing(false);
    })();
  }, [selectedProductId, products]);

  const handleSaveBrief = async () => {
    if (!selectedProductId) return;
    setSaving(true);
    const product = products.find(p => p.id === selectedProductId);
    await service.updateProductBrief(selectedProductId, briefText || undefined, product?.store);
    const result = await service.getProductBrief(selectedProductId);
    if (result?.brief) setBrief(result.brief);
    setSaving(false);
  };

  const handleSaveMarketingBrief = async (updated: MarketingBrief) => {
    if (!selectedProductId) return;
    setSavingBrief(true);
    const result = await service.updateMarketingBrief(selectedProductId, updated);
    if (result?.brief) {
      setMarketingBrief(result.brief);
      setMarketingBriefMeta({
        version: result.version ?? null,
        editSource: result.editSource ?? null,
        updatedAt: result.updatedAt ?? null,
      });
    }
    setSavingBrief(false);
  };

  /**
   * Re-run pattern recommendations against the currently selected product
   * with any filter inputs the user has tuned. The initial query (fired
   * from the product-select effect) is always unfiltered; this handler is
   * only wired to the "Re-run" button on top of the table.
   */
  const handleRerunPatterns = async () => {
    if (!selectedProductId) return;
    setPatternsLoading(true);
    setPatternsError(null);
    const parsedGrade = patternFilterMinGrade.trim() === '' ? NaN : parseFloat(patternFilterMinGrade);
    const result = await service.getPatternRecommendations(selectedProductId, {
      limit: 10,
      startDateFrom: patternFilterStartFrom || undefined,
      startDateTo: patternFilterStartTo || undefined,
      minGrade: Number.isFinite(parsedGrade) ? parsedGrade : undefined,
    });
    if (result) setPatterns(result);
    else setPatternsError('Failed to load pattern recommendations.');
    setPatternsLoading(false);
  };

  // Clear search state when the product changes so a stale result set from
  // the previous product never bleeds into the new product's table.
  useEffect(() => {
    setAddedAds({});
    setSearchResults([]);
    setSearchTotal(0);
    setSearchOffset(0);
    setTableFilterText('');
    setAdIdInput('');
    setAdIdLookupError(null);
    setSearchOpen(false);
  }, [selectedProductId]);

  const runSearchAds = async (offset: number) => {
    setSearchLoading(true);
    const result = await service.listAds({
      hookDevice: searchHookDevice || undefined,
      primaryEmotion: searchEmotion || undefined,
      tone: searchTone || undefined,
      persuasionAngle: searchPersuasionAngle || undefined,
      limit: SEARCH_PAGE_SIZE,
      offset,
    });
    if (result) {
      setSearchResults(result.ads);
      setSearchTotal(result.total);
      setSearchOffset(offset);
    }
    setSearchLoading(false);
  };

  const handleApplyFilters = () => runSearchAds(0);

  const handleAddAd = (ad: CIAd) => {
    setAddedAds(prev => prev[ad.adId] ? prev : { ...prev, [ad.adId]: ad });
  };

  const handleRemoveAddedAd = (adId: string) => {
    setAddedAds(prev => {
      if (!(adId in prev)) return prev;
      const next = { ...prev };
      delete next[adId];
      return next;
    });
  };

  const handleAddById = async () => {
    const id = adIdInput.trim();
    if (!id) return;
    setAdIdLookupError(null);
    setAdIdLookupLoading(true);
    const detail = await service.getAd(id);
    if (!detail) {
      setAdIdLookupError(`No ad found for "${id}".`);
    } else {
      // CIAdDetail nests features under .features; project the four columns
      // we render onto the CIAd shape stored in `addedAds`.
      const adWithFeatures: CIAd = {
        ...detail,
        hookDevice: detail.features?.hookDevice ?? null,
        persuasionAngle: detail.features?.persuasionAngle ?? null,
        primaryEmotion: detail.features?.primaryEmotion ?? null,
        tone: detail.features?.tone ?? null,
        narrativeStructure: detail.features?.narrativeStructure ?? null,
        proofType: detail.features?.proofType ?? null,
      };
      handleAddAd(adWithFeatures);
      setAdIdInput('');
    }
    setAdIdLookupLoading(false);
  };

  const handleToggleTemplateAd = async (adId: string) => {
    if (templateAds[adId]) {
      // Remove — clear every per-template state slice.
      const dropAd = (prev: Record<string, unknown>) => {
        const next = { ...prev }; delete next[adId]; return next;
      };
      setTemplateAds(prev => dropAd(prev) as Record<string, CIAdDetail>);
      setTemplateElementMap(prev => dropAd(prev) as Record<string, TemplateElementKey[]>);
      setTemplateElementOverrides(prev => dropAd(prev) as Record<string, Partial<Record<TemplateElementKey, string>>>);
      setTemplateTranscripts(prev => dropAd(prev) as Record<string, string>);
      setTemplatePatterns(prev => dropAd(prev) as Record<string, TemplatePatternMap>);
      setTemplateFeatureSummaries(prev => dropAd(prev) as Record<string, FeatureSummaryKey[]>);
      setTemplateSegments(prev => dropAd(prev) as Record<string, string[]>);
      return;
    }
    // Add — fetch full detail for chips + features + segments. Surface a
    // spinner in the table cell while this is in flight; it can take a
    // couple of seconds since the CIAdDetail call is heavy.
    setTogglingTemplateAdId(adId);
    let detail: CIAdDetail | null = null;
    try {
      detail = await service.getAd(adId);
    } finally {
      setTogglingTemplateAdId(null);
    }
    if (!detail) return;
    const patterns = defaultPatternsForAd(detail);
    const summaries = defaultFeatureSummariesForAd(detail);
    const segments = defaultSegmentsForAd(detail);
    setTemplateAds(prev => ({ ...prev, [adId]: detail! }));
    setTemplateElementMap(prev => ({ ...prev, [adId]: [...DEFAULT_TEMPLATE_ELEMENTS] }));
    setTemplatePatterns(prev => ({ ...prev, [adId]: patterns }));
    setTemplateFeatureSummaries(prev => ({ ...prev, [adId]: summaries }));
    setTemplateSegments(prev => ({ ...prev, [adId]: segments }));
    // Seed the target duration from this ad's actual clip length so the
    // generated script comes out roughly the same length as the original —
    // but never clobber a duration the user has typed themselves.
    const adDuration = totalDurationForAd(detail);
    if (adDuration != null && !durationTouchedRef.current) setTargetDuration(adDuration);
    // Fetch the verbatim transcript so the per-element preview can show
    // actual spoken lines (sliced via segment lineStart/lineEnd) instead
    // of the analysis-style transcriptExcerpt/summary on each segment.
    if (detail.transcriptUrl) {
      fetch(detail.transcriptUrl)
        .then(r => r.text())
        .then(t => setTemplateTranscripts(prev => ({ ...prev, [adId]: t })))
        .catch(() => { /* leave it unset — preview falls back to segment text */ });
    }
  };


  const handleToggleTemplateElement = (adId: string, key: TemplateElementKey) => {
    setTemplateElementMap(prev => {
      const current = prev[adId] || [];
      const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
      return { ...prev, [adId]: next };
    });
  };

  const handleSetTemplateElementOverride = (
    adId: string,
    key: TemplateElementKey,
    text: string,
  ) => {
    setTemplateElementOverrides(prev => {
      const adOverrides = { ...(prev[adId] || {}) };
      if (text === '') {
        delete adOverrides[key];
      } else {
        adOverrides[key] = text;
      }
      const next = { ...prev };
      if (Object.keys(adOverrides).length === 0) delete next[adId];
      else next[adId] = adOverrides;
      return next;
    });
  };

  // Assemble the wire-shape StoryboardConfig from current UI state. Shared
  // by the actual-generation path and the prompt-preview path so the
  // preview is guaranteed to match what generation would send.
  const buildStoryboardConfig = (): StoryboardConfig => ({
    hookDevice: null,
    narrativeStructure: null,
    productionFormat: null,
    tone: null,
    proofType: null,
    ctaType: null,
    targetDurationSec: targetDuration,
    constraints: constraints.trim(),
    templateAdIds,
    templateElementMap,
    templateElementOverrides,
    includeProductBrief: includeProductBriefInPrompt,
    marketingBriefSections: includedBriefSections,
    outputFormats,
    templatePatterns,
    templateFeatureSummaries,
    templateSegments,
    marketingBriefSelection,
    audienceSelection,
    angleSelection,
    model: generationModel,
  });

  const handleGenerateFromTemplates = async () => {
    if (!selectedProductId || templateAdIds.length === 0) return;
    setGenerating(true);
    // Generation is async now — the backend saves a request and either
    // enqueues a storyboard-generation job (templates ready) or kicks off
    // the pipeline for un-extracted templates and marks the row waiting.
    // The result is visible in the Scripts tab.
    const result = await service.generateStoryboard(selectedProductId, buildStoryboardConfig());
    setGenerating(false);
    if (result?.storyboardId) {
      const n = result.adsNeedingPipeline?.length || 0;
      setSavedMsg(n > 0
        ? `Generation queued — ${n} template${n === 1 ? '' : 's'} need transcription + extraction first. View progress in the Scripts tab.`
        : 'Generation queued — view progress in the Scripts tab.');
      // Refresh the template-usage map so the just-used reference ad
      // immediately dims in the matched-ads table.
      loadTemplateUsage();
    }
  };

  // Prompt-preview state. The dialog shows the assembled LLM prompt before
  // any generation runs; the user can "Generate" from inside the dialog to
  // confirm and kick off the real generation flow.
  const [promptPreviewOpen, setPromptPreviewOpen] = useState(false);
  const [promptPreviewText, setPromptPreviewText] = useState<string | null>(null);
  const [promptPreviewModel, setPromptPreviewModel] = useState<string | null>(null);
  const [promptPreviewLoading, setPromptPreviewLoading] = useState(false);
  const [promptPreviewError, setPromptPreviewError] = useState<string | null>(null);
  const [savingRequest, setSavingRequest] = useState(false);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  const handlePreviewPrompt = async () => {
    if (!selectedProductId || templateAdIds.length === 0) return;
    setPromptPreviewOpen(true);
    setPromptPreviewLoading(true);
    setPromptPreviewError(null);
    setPromptPreviewText(null);
    setPromptPreviewModel(null);
    try {
      const result = await service.previewStoryboardPrompt(selectedProductId, buildStoryboardConfig());
      if (result?.prompt) {
        setPromptPreviewText(result.prompt);
        setPromptPreviewModel(result.model);
      } else {
        setPromptPreviewError('Preview returned no prompt.');
      }
    } catch (err) {
      setPromptPreviewError(err instanceof Error ? err.message : String(err));
    } finally {
      setPromptPreviewLoading(false);
    }
  };

  const handleGenerateFromPreview = async () => {
    setPromptPreviewOpen(false);
    await handleGenerateFromTemplates();
  };

  // Save the configured request without running generation — captures the
  // config + prompt as a SAVED storyboard to generate later from the Scripts
  // tab. Same config builder as Generate, so the two paths never drift.
  const handleSaveRequest = async () => {
    if (!selectedProductId || templateAdIds.length === 0) return;
    setSavingRequest(true);
    const result = await service.saveStoryboardRequest(selectedProductId, buildStoryboardConfig());
    setSavingRequest(false);
    if (result?.storyboardId) {
      setPromptPreviewOpen(false);
      setSavedMsg('Request saved — generate it later from the Scripts tab.');
    }
  };

  const history = useHistory();

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>Script Builder</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select a product to discover which competitor creative patterns are most relevant, then generate a storyboard.
      </Typography>

      {/* Product selector — searchable */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Autocomplete
          size="small"
          options={products}
          value={products.find(p => p.id === selectedProductId) || null}
          onChange={(_, next) => setSelectedProductId(next ? next.id : '')}
          getOptionLabel={p => `${p.title}${p.store ? ` (${p.store})` : ''}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          loading={productsLoading}
          loadingText="Loading products…"
          disabled={productsLoading}
          filterOptions={createFilterOptions({
            // Token-based substring match across title, handle, store, tags,
            // category, vendor. `matchFrom: 'any'` lets each typed token match
            // anywhere in the joined label (good enough for "fuzzy as I type"
            // without bringing in a dependency).
            matchFrom: 'any',
            stringify: (p: Product) => [p.title, p.handle, p.store, p.tags, p.category, p.vendor]
              .filter(Boolean).join(' '),
            trim: true,
          })}
          renderOption={(props, p) => (
            <Box component="li" {...props} key={p.id} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start !important', py: 0.75 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                <Typography variant="body2" sx={{ flexGrow: 1 }}>{p.title}</Typography>
                {p.store && <Chip label={p.store} size="small" variant="outlined" />}
              </Box>
              {(p.category || p.productType) && (
                <Typography variant="caption" color="text.secondary">
                  {p.category || p.productType}
                </Typography>
              )}
            </Box>
          )}
          renderInput={params => (
            <TextField {...params}
              label={productsLoading ? 'Loading products…' : 'Search products'}
              placeholder="Type to filter…"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {productsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Paper>

      {/* Combined initializing state — once a product is selected, hold off
          on rendering the brief / patterns / table panels until BOTH the
          marketing brief and the auto-fired pattern-recommendations fetch
          have resolved, so the content area appears as a single coordinated
          render instead of staggering each panel in. */}
      {selectedProductId && initializing && (
        <Paper variant="outlined" sx={{ p: 4, mb: 2, textAlign: 'center' }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Loading marketing brief and pattern recommendations…
          </Typography>
        </Paper>
      )}

      {/* Marketing Brief viewer (primary when present). */}
      {selectedProductId && !initializing && marketingBrief && (
        <MarketingBriefViewer
          brief={marketingBrief}
          version={marketingBriefMeta.version ?? null}
          editSource={marketingBriefMeta.editSource ?? null}
          saving={savingBrief}
          collapsed={briefCollapsed}
          onToggleCollapsed={() => setBriefCollapsed(c => !c)}
          onSave={handleSaveMarketingBrief}
        />
      )}

      {/* Active promotions panel. Auto-loaded for the selected product;
          refreshed daily by the background cron, plus a manual refresh
          button here for immediate re-fetches. */}
      {selectedProductId && !initializing && (
        <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: promotions.length > 0 ? 1 : 0, flexWrap: 'wrap' }}>
            <Typography variant="subtitle2">Active promotions</Typography>
            <Chip size="small" variant="outlined"
              label={promotionsLoading ? 'loading…' : `${promotions.length}`}
              sx={{ height: 20, fontSize: '0.7rem' }} />
            <Box sx={{ flexGrow: 1 }} />
            <Typography variant="caption" color="text.secondary">
              Refreshed daily · forced refresh fetches from Shopify + PDP.
            </Typography>
            <Button size="small" variant="outlined"
              disabled={promotionsRefreshing}
              onClick={handleRefreshPromotions}>
              {promotionsRefreshing ? <><CircularProgress size={14} sx={{ mr: 1 }} />Refreshing…</> : 'Refresh'}
            </Button>
          </Box>
          {promotionsError && <Alert severity="error" sx={{ mt: 1 }}>{promotionsError}</Alert>}
          {!promotionsLoading && !promotionsRefreshing && promotions.length === 0 && (
            <Typography variant="caption" color="text.disabled">
              No active promotions for this product. Storyboards will be generated without promo lines.
            </Typography>
          )}
          {promotions.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {promotions.map(p => (
                <Box key={p.promotionId} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip size="small" variant="outlined"
                    label={p.source.replace('_', ' ')}
                    sx={{ height: 20, fontSize: '0.65rem' }} />
                  <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0 }}>
                    {p.displayText}
                  </Typography>
                  {p.endsAt && (
                    <Typography variant="caption" color="text.secondary">
                      ends {new Date(p.endsAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}

      {/* Product brief editor — fallback when no marketing brief is available. */}
      {selectedProductId && !initializing && !marketingBrief && (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          {briefLoading ? <CircularProgress size={20} /> : brief && !briefEditorExpanded ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle2">Product Brief</Typography>
              <Chip label={brief.briefSource} size="small" variant="outlined" color="success" />
              <Typography variant="caption" color="text.secondary">Brief loaded.</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button size="small" onClick={() => setBriefEditorExpanded(true)}>Edit brief</Button>
            </Box>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Product Brief
                {brief && <Chip label={brief.briefSource} size="small" sx={{ ml: 1 }} variant="outlined" />}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Describe the product, target audience, key benefits, and pain points.
              </Typography>
              <TextField multiline fullWidth minRows={3} maxRows={8} value={briefText}
                onChange={e => setBriefText(e.target.value)} variant="outlined" size="small" sx={{ mb: 1 }}
                placeholder="e.g., Premium sleep supplement for women 25-45. Key benefit: fall asleep faster without grogginess."
              />
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button size="small" variant="contained" onClick={handleSaveBrief} disabled={saving}>
                  {saving ? 'Saving...' : brief ? 'Update Brief' : 'Save Brief'}
                </Button>
                {brief && briefEditorExpanded && (
                  <Button size="small" onClick={() => setBriefEditorExpanded(false)}>Collapse</Button>
                )}
              </Box>
            </>
          )}
        </Paper>
      )}

      {/* Pattern-recommendations failure — shown in place of the matched-ads
          table (which only renders once patterns load). */}
      {selectedProductId && !initializing && patternsError && (
        <Alert severity="error" sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" disabled={patternsLoading}
              onClick={handleRerunPatterns}>
              {patternsLoading ? <CircularProgress size={14} color="inherit" /> : 'Retry'}
            </Button>
          }>
          {patternsError}
        </Alert>
      )}

      {/* Pattern results — the standalone "Creative Patterns" distribution
          panel was removed; the same color coding is now inlined into the
          ads table below (color per pattern value, with a hover breakdown). */}
      {patterns && (
        <>
          {/* Matched ads table with selection checkboxes */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Box onClick={() => setMatchedAdsCollapsed(c => !c)}
              role="button" tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMatchedAdsCollapsed(c => !c); } }}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: matchedAdsCollapsed ? 0 : 1, gap: 1, flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setMatchedAdsCollapsed(c => !c); }}
                  aria-label={matchedAdsCollapsed ? 'Expand ads table' : 'Collapse ads table'}>
                  {matchedAdsCollapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
                <Typography variant="subtitle2">Most Relevant Competitor Ads</Typography>
                <Chip size="small" variant="outlined"
                  label={`${combinedAds.length} ad${combinedAds.length === 1 ? '' : 's'}`} />
                {Object.keys(addedAds).length > 0 && (
                  <Chip size="small" variant="outlined" color="info"
                    label={`+${Object.keys(addedAds).length} added`} />
                )}
                {templateAdIds.length > 0 && (
                  <Chip size="small" color="primary" variant="outlined"
                    label={`${templateAdIds.length} template${templateAdIds.length === 1 ? '' : 's'}`} />
                )}
              </Box>
            </Box>
            <Collapse in={!matchedAdsCollapsed} timeout="auto" unmountOnExit>
            {marketingBriefLoading ? (
              <Alert severity="info" sx={{ mb: 1 }}>Checking for a marketing brief…</Alert>
            ) : !marketingBrief ? (
              <Alert severity="warning" sx={{ mb: 1 }}>
                No marketing brief found for this product. Sync one with the marketing-brief pipeline to enable storyboard generation.
              </Alert>
            ) : null}

            {/* Re-run pattern recommendations — sits directly above the
                table so the user can re-query with extra constraints. The
                initial fetch fired by the product-select effect is
                intentionally unfiltered (across all ads); these filters
                only apply when the user clicks Re-run. */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
              <Tooltip title="Re-runs cosine similarity against your product brief over CI_ADS (with the filters applied below, if any) and replaces the recommended ads in the table.">
                <span>
                  <Button size="small" variant="contained" onClick={handleRerunPatterns}
                    disabled={patternsLoading || initializing || !selectedProductId}>
                    {patternsLoading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
                    Re-run Pattern Recommendations
                  </Button>
                </span>
              </Tooltip>
              <Button size="small" variant="outlined"
                onClick={() => setPatternFiltersOpen(o => !o)}>
                {patternFiltersOpen ? 'Hide filters' : 'Filters'}
              </Button>
              {(patternFilterStartFrom || patternFilterStartTo || patternFilterMinGrade) && (
                <Chip size="small" color="primary" variant="outlined"
                  label={`${[
                    patternFilterStartFrom ? `from ${patternFilterStartFrom}` : null,
                    patternFilterStartTo ? `to ${patternFilterStartTo}` : null,
                    patternFilterMinGrade ? `≥ grade ${patternFilterMinGrade}` : null,
                  ].filter(Boolean).join(' · ')}`} />
              )}
            </Box>
            <Collapse in={patternFiltersOpen} timeout="auto" unmountOnExit>
              <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#fafafa' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Restrict the pool of ads scored against your brief. Applied on the next Re-run.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <TextField size="small" label="Start date from" type="date"
                    InputLabelProps={{ shrink: true }}
                    value={patternFilterStartFrom}
                    onChange={e => setPatternFilterStartFrom(e.target.value)}
                    sx={{ width: 180 }} />
                  <TextField size="small" label="Start date to" type="date"
                    InputLabelProps={{ shrink: true }}
                    value={patternFilterStartTo}
                    onChange={e => setPatternFilterStartTo(e.target.value)}
                    sx={{ width: 180 }} />
                  <TextField size="small" label="Minimum grade" type="number"
                    placeholder="e.g., 7"
                    inputProps={{ min: 0, max: 10, step: 0.5 }}
                    value={patternFilterMinGrade}
                    onChange={e => setPatternFilterMinGrade(e.target.value)}
                    sx={{ width: 160 }} />
                  {(patternFilterStartFrom || patternFilterStartTo || patternFilterMinGrade) && (
                    <Button size="small" onClick={() => {
                      setPatternFilterStartFrom('');
                      setPatternFilterStartTo('');
                      setPatternFilterMinGrade('');
                    }}>Clear</Button>
                  )}
                </Box>
              </Paper>
            </Collapse>

            {/* Search & filter — find arbitrary ads to add to the table */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
              <TextField size="small" placeholder="Filter rows by ad ID, page, or headline"
                value={tableFilterText} onChange={e => setTableFilterText(e.target.value)}
                sx={{ minWidth: 280, flex: '1 1 280px' }} />
              <Button size="small" variant="outlined" onClick={() => setSearchOpen(o => !o)}>
                {searchOpen ? 'Hide search' : 'Search ads to add'}
              </Button>
            </Box>
            <Collapse in={searchOpen} timeout="auto" unmountOnExit>
              <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#fafafa' }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Pull additional ads into the table by exact ad ID, or by filtering CI_ADS server-side.
                </Typography>

                {/* Add by ad ID */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <TextField size="small" label="Add by ad ID" placeholder="paste ad UUID"
                    value={adIdInput} onChange={e => setAdIdInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleAddById(); }}
                    sx={{ minWidth: 320 }} />
                  <Button size="small" variant="contained" onClick={handleAddById}
                    disabled={adIdLookupLoading || !adIdInput.trim()}>
                    {adIdLookupLoading ? <CircularProgress size={16} /> : 'Add'}
                  </Button>
                  {adIdLookupError && <Typography variant="caption" color="error">{adIdLookupError}</Typography>}
                </Box>

                {/* Server-side filter. Dropdowns are fed from CI_ENUM_VALUES
                    (freeSolo, so custom values still work). */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  {([
                    { label: 'Hook device', enumType: 'hook_device', value: searchHookDevice, setValue: setSearchHookDevice, width: 180 },
                    { label: 'Emotion', enumType: 'primary_emotion', value: searchEmotion, setValue: setSearchEmotion, width: 160 },
                    { label: 'Tone', enumType: 'tone', value: searchTone, setValue: setSearchTone, width: 160 },
                    { label: 'Persuasion angle', enumType: 'persuasion_angle', value: searchPersuasionAngle, setValue: setSearchPersuasionAngle, width: 160 },
                  ] as const).map(({ label, enumType, value, setValue, width }) => (
                    <Autocomplete<string, false, false, true>
                      key={enumType}
                      size="small" freeSolo
                      options={(enumsByType[enumType] || []).map(e => e.enumValue)}
                      value={value}
                      inputValue={value}
                      onInputChange={(_, v) => setValue(v)}
                      sx={{ minWidth: width }}
                      renderInput={p => <TextField {...p} label={label} />}
                    />
                  ))}
                  <Button size="small" variant="contained" onClick={handleApplyFilters} disabled={searchLoading}>
                    {searchLoading ? <CircularProgress size={16} /> : 'Search'}
                  </Button>
                  {(searchHookDevice || searchEmotion || searchTone || searchPersuasionAngle) && (
                    <Button size="small" onClick={() => {
                      setSearchHookDevice(''); setSearchEmotion(''); setSearchTone(''); setSearchPersuasionAngle('');
                    }}>Clear filters</Button>
                  )}
                </Box>

                {searchResults.length > 0 && (
                  <>
                    <TableContainer sx={{ maxHeight: 360, mt: 1 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell width={70}></TableCell>
                            <TableCell>Page</TableCell>
                            <TableCell>Headline</TableCell>
                            <TableCell>Hook Device</TableCell>
                            <TableCell>Emotion</TableCell>
                            <TableCell>Persuasion Angle</TableCell>
                            <TableCell>Proof</TableCell>
                            <TableCell width={70}>Grade</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {searchResults.map(ad => {
                            const alreadyInTable = combinedAds.some(a => a.adId === ad.adId);
                            return (
                              <TableRow key={ad.adId} hover
                                onClick={() => setSelectedAdId(ad.adId)}
                                sx={{ cursor: 'pointer' }}>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  {alreadyInTable ? (
                                    <Chip size="small" label="In list" variant="outlined" />
                                  ) : (
                                    <Button size="small" variant="outlined" onClick={() => handleAddAd(ad)}>
                                      Add
                                    </Button>
                                  )}
                                </TableCell>
                                <TableCell>{ad.pageName || '-'}</TableCell>
                                <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {ad.headline || '-'}
                                </TableCell>
                                <TableCell>{ad.hookDevice ? formatEnumLabel(ad.hookDevice) : '-'}</TableCell>
                                <TableCell>{ad.primaryEmotion ? formatEnumLabel(ad.primaryEmotion) : '-'}</TableCell>
                                <TableCell>{ad.persuasionAngle ? formatEnumLabel(ad.persuasionAngle) : '-'}</TableCell>
                                <TableCell>{ad.proofType ? formatEnumLabel(ad.proofType) : '-'}</TableCell>
                                <TableCell>{ad.overallGrade || '-'}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {searchOffset + 1}–{Math.min(searchOffset + searchResults.length, searchTotal)} of {searchTotal}
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      <Button size="small" disabled={searchLoading || searchOffset === 0}
                        onClick={() => runSearchAds(Math.max(0, searchOffset - SEARCH_PAGE_SIZE))}>
                        ← Prev
                      </Button>
                      <Button size="small" disabled={searchLoading || searchOffset + SEARCH_PAGE_SIZE >= searchTotal}
                        onClick={() => runSearchAds(searchOffset + SEARCH_PAGE_SIZE)}>
                        Next →
                      </Button>
                    </Box>
                  </>
                )}
              </Paper>
            </Collapse>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={120}>Use as template</TableCell>
                    <TableCell width={120}>Source</TableCell>
                    <TableCell width={90}>Used</TableCell>
                    <TableCell>
                      <TableSortLabel active={matchedSortField === 'pageName'} direction={matchedSortField === 'pageName' ? matchedSortDir : 'asc'} onClick={() => handleMatchedSort('pageName' as never)}>
                        Page
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={matchedSortField === 'headline'} direction={matchedSortField === 'headline' ? matchedSortDir : 'asc'} onClick={() => handleMatchedSort('headline' as never)}>
                        Headline
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={matchedSortField === 'hookDevice'} direction={matchedSortField === 'hookDevice' ? matchedSortDir : 'asc'} onClick={() => handleMatchedSort('hookDevice' as never)}>
                        Hook Device
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={matchedSortField === 'primaryEmotion'} direction={matchedSortField === 'primaryEmotion' ? matchedSortDir : 'asc'} onClick={() => handleMatchedSort('primaryEmotion' as never)}>
                        Emotion
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={matchedSortField === 'persuasionAngle'} direction={matchedSortField === 'persuasionAngle' ? matchedSortDir : 'asc'} onClick={() => handleMatchedSort('persuasionAngle' as never)}>
                        Persuasion Angle
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={matchedSortField === 'proofType'} direction={matchedSortField === 'proofType' ? matchedSortDir : 'asc'} onClick={() => handleMatchedSort('proofType' as never)}>
                        Proof
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={matchedSortField === 'score'} direction={matchedSortField === 'score' ? matchedSortDir : 'asc'} onClick={() => handleMatchedSort('score' as never)}>
                        Relevance
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={matchedSortField === 'overallGrade'} direction={matchedSortField === 'overallGrade' ? matchedSortDir : 'asc'} onClick={() => handleMatchedSort('overallGrade' as never)}>
                        Grade
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel active={matchedSortField === 'startDate'} direction={matchedSortField === 'startDate' ? matchedSortDir : 'asc'} onClick={() => handleMatchedSort('startDate' as never)}>
                        Started
                      </TableSortLabel>
                    </TableCell>
                    <TableCell width={60}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedMatchedAds.map(ad => {
                    // Helper: render a pattern cell as plain colored text
                    // (green/blue/gray depending on the value's dominance
                    // in the matched pool). The hover tooltip carries the
                    // field's full distribution breakdown.
                    const patternCell = (field: string, value: string | null) => {
                      const dist = patterns?.patternSummary?.[field];
                      const color = patternColorForValue(dist, value);
                      const breakdown = patternBreakdownTooltip(field, dist);
                      if (!value) {
                        return (
                          <Tooltip title={breakdown} arrow placement="top">
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          </Tooltip>
                        );
                      }
                      return (
                        <Tooltip title={breakdown} arrow placement="top">
                          <Typography component="span" variant="body2"
                            sx={{ color: color ?? 'text.secondary', fontWeight: 600 }}>
                            {formatEnumLabel(value)}
                          </Typography>
                        </Tooltip>
                      );
                    };
                    const isToggling = togglingTemplateAdId === ad.adId;
                    return (
                    <TableRow key={ad.adId} hover
                      onClick={() => setSelectedAdId(ad.adId)}
                      sx={{ cursor: 'pointer', opacity: templateUsage[ad.adId] ? 0.55 : 1 }}>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Switch size="small"
                            checked={Boolean(templateAds[ad.adId])}
                            disabled={isToggling}
                            onChange={() => handleToggleTemplateAd(ad.adId)} />
                          {isToggling && <CircularProgress size={14} />}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {ad.source === 'recommended' ? (
                          <Chip size="small" color="primary" variant="outlined" label="Recommended" />
                        ) : (
                          <Chip size="small" color="info" variant="outlined" label="Added" />
                        )}
                      </TableCell>
                      <TableCell>
                        {templateUsage[ad.adId] ? (
                          <Tooltip arrow placement="top" title={
                            <Box sx={{ p: 0.5, maxWidth: 280 }}>
                              <Typography variant="caption" fontWeight={700} sx={{ display: 'block', color: '#fff', mb: 0.5 }}>
                                Already used as template
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: '#fff' }}>
                                Used in {templateUsage[ad.adId].count} generated storyboard{templateUsage[ad.adId].count === 1 ? '' : 's'}.
                                {templateUsage[ad.adId].lastUsedAt && (
                                  <> Last used {new Date(templateUsage[ad.adId].lastUsedAt as string).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}.</>
                                )}
                              </Typography>
                            </Box>
                          }>
                            <Chip size="small" color="warning" variant="outlined"
                              label={`${templateUsage[ad.adId].count}×`}
                              sx={{ height: 22, fontWeight: 600 }} />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>{ad.pageName || '-'}</TableCell>
                      <TableCell sx={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ad.headline || '-'}
                      </TableCell>
                      <TableCell>{patternCell('hookDevice', ad.hookDevice)}</TableCell>
                      <TableCell>{patternCell('primaryEmotion', ad.primaryEmotion)}</TableCell>
                      <TableCell>{patternCell('persuasionAngle', ad.persuasionAngle)}</TableCell>
                      <TableCell>{patternCell('proofType', ad.proofType)}</TableCell>
                      <TableCell>
                        {ad.score != null ? (
                          <Tooltip arrow placement="top" title={
                            <Box sx={{ p: 0.5, maxWidth: 320 }}>
                              <Typography variant="caption" fontWeight={700} sx={{ display: 'block', color: '#fff', mb: 0.5 }}>
                                Relevance score
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: '#fff' }}>
                                Cosine similarity between this product's brief embedding and this ad's embedding, on a 0–100% scale. Higher means the ad is a closer semantic match to the product brief. Recommended ads are ranked by this score.
                              </Typography>
                            </Box>
                          }>
                            <Chip label={`${Math.round(ad.score * 100)}%`} size="small"
                              color={ad.score > 0.85 ? 'success' : ad.score > 0.7 ? 'info' : 'default'} />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {ad.overallGrade != null ? (
                          <Tooltip arrow placement="top" title={
                            <Box sx={{ p: 0.5, maxWidth: 320 }}>
                              <Typography variant="caption" fontWeight={700} sx={{ display: 'block', color: '#fff', mb: 0.5 }}>
                                Overall grade
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', color: '#fff' }}>
                                Claude's holistic rating of this ad (0–10) from the analysis pass: hook strength, problem/solution clarity, proof quality, pacing, and CTA. Higher = better-performing reference; aim for 7+ when picking templates.
                              </Typography>
                            </Box>
                          }>
                            <Typography component="span" variant="body2">{ad.overallGrade}</Typography>
                          </Tooltip>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {ad.startDate ? (
                          <Typography variant="caption" color="text.secondary">
                            {new Date(ad.startDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {ad.source === 'added' && (
                          <Tooltip title="Remove from list">
                            <IconButton size="small" onClick={() => handleRemoveAddedAd(ad.adId)}>
                              <CloseIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                  {sortedMatchedAds.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={13}>
                        <Typography variant="caption" color="text.secondary">
                          {tableFilterText ? 'No rows match your filter.' : 'No ads yet — search above to add some.'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {/* Pagination footer — 8 rows per page. Hidden if everything
                fits on one page. */}
            {sortedMatchedAds.length > MATCHED_PAGE_SIZE && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  {matchedPageStart + 1}–{Math.min(matchedPageStart + MATCHED_PAGE_SIZE, sortedMatchedAds.length)} of {sortedMatchedAds.length}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" disabled={matchedPage === 0}
                  onClick={() => setMatchedPage(p => Math.max(0, p - 1))}>
                  ← Prev
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Page {matchedPage + 1} / {matchedPageCount}
                </Typography>
                <Button size="small" disabled={matchedPage >= matchedPageCount - 1}
                  onClick={() => setMatchedPage(p => Math.min(matchedPageCount - 1, p + 1))}>
                  Next →
                </Button>
              </Box>
            )}
            </Collapse>
          </Paper>

          {/* Template Preview */}
          {marketingBrief && templateAdIds.length > 0 && (
            <TemplatePreview
              templateAdIds={templateAdIds}
              templateAds={templateAds}
              templateElementMap={templateElementMap}
              templateElementOverrides={templateElementOverrides}
              templateTranscripts={templateTranscripts}
              templatePatterns={templatePatterns}
              templateFeatureSummaries={templateFeatureSummaries}
              templateSegments={templateSegments}
              marketingBriefSelection={marketingBriefSelection}
              audienceSelection={audienceSelection}
              angleSelection={angleSelection}
              enumsByType={enumsByType}
              marketingBrief={marketingBrief}
              productBrief={brief}
              selectedAngleIdx={selectedAngleIdx}
              targetDuration={targetDuration}
              generationModel={generationModel}
              onGenerationModelChange={setGenerationModel}
              constraints={constraints}
              generating={generating}
              includeProductBrief={includeProductBriefInPrompt}
              includedBriefSections={includedBriefSections}
              onIncludeProductBriefChange={setIncludeProductBriefInPrompt}
              onIncludedBriefSectionsChange={setIncludedBriefSections}
              onRemoveTemplate={handleToggleTemplateAd}
              onToggleElement={handleToggleTemplateElement}
              onSetElementOverride={handleSetTemplateElementOverride}
              onTemplatePatternsChange={setTemplatePatterns}
              onTemplateFeatureSummariesChange={setTemplateFeatureSummaries}
              onTemplateSegmentsChange={setTemplateSegments}
              onMarketingBriefSelectionChange={setMarketingBriefSelection}
              onAudienceSelectionChange={setAudienceSelection}
              onAngleSelectionChange={setAngleSelection}
              onAngleChange={(idx) => setSelectedAngleIdx(idx)}
              onTargetDurationChange={(n) => { durationTouchedRef.current = true; setTargetDuration(n); }}
              onConstraintsChange={setConstraints}
              onGenerate={handleGenerateFromTemplates}
              onPreviewPrompt={handlePreviewPrompt}
            />
          )}
        </>
      )}

      <AdDetailDialog
        adId={selectedAdId}
        onClose={() => setSelectedAdId(null)}
      />

      {/* Preview LLM Prompt dialog — assembled by the same backend helper
          that `generateStoryboard` uses, so the user sees byte-identical
          input to what Claude would receive. The Generate button confirms
          and kicks off the real generation flow. */}
      <Dialog open={promptPreviewOpen} onClose={() => setPromptPreviewOpen(false)}
        maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" component="span" fontWeight={600}>
              LLM prompt preview
            </Typography>
            {promptPreviewModel && (
              <Chip size="small" variant="outlined" label={promptPreviewModel} />
            )}
            {promptPreviewText && (
              <Chip size="small" variant="outlined"
                label={`${promptPreviewText.length.toLocaleString()} chars`} />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            This is the exact prompt that will be sent to Claude. Both Preview and Generate go through the same backend helper, so what you see here is what gets sent.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          {promptPreviewLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Assembling prompt…</Typography>
            </Box>
          )}
          {promptPreviewError && (
            <Alert severity="error" sx={{ mb: 1 }}>{promptPreviewError}</Alert>
          )}
          {promptPreviewText && (
            <Box sx={{ bgcolor: '#fafafa', p: 1.5, borderRadius: 1,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: '0.8rem', lineHeight: 1.55, whiteSpace: 'pre-wrap',
              maxHeight: '60vh', overflow: 'auto' }}>
              {promptPreviewText}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {promptPreviewText && (
            <Tooltip title="Copy prompt to clipboard">
              <IconButton size="small"
                onClick={() => navigator.clipboard.writeText(promptPreviewText)}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setPromptPreviewOpen(false)}>Close</Button>
          <Button
            disabled={!promptPreviewText || savingRequest || generating}
            onClick={handleSaveRequest}>
            {savingRequest ? <><CircularProgress size={14} sx={{ mr: 1 }} /> Saving…</> : 'Save request'}
          </Button>
          <Button variant="contained"
            disabled={!promptPreviewText || generating || savingRequest}
            onClick={handleGenerateFromPreview}>
            {generating ? <><CircularProgress size={14} sx={{ mr: 1 }} /> Generating…</> : 'Generate storyboard'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!savedMsg}
        autoHideDuration={10000}
        onClose={() => setSavedMsg(null)}
        message={savedMsg}
        action={
          <Button color="inherit" size="small"
            onClick={() => { setSavedMsg(null); history.push('/ad-library/scripts'); }}>
            View in Scripts
          </Button>
        }
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

// ── Template Preview component ──

interface TemplatePreviewProps {
  templateAdIds: string[];
  templateAds: Record<string, CIAdDetail>;
  templateElementMap: Record<string, TemplateElementKey[]>;
  templateElementOverrides: Record<string, Partial<Record<TemplateElementKey, string>>>;
  templateTranscripts: Record<string, string>;
  templatePatterns: Record<string, TemplatePatternMap>;
  templateFeatureSummaries: Record<string, FeatureSummaryKey[]>;
  templateSegments: Record<string, string[]>;
  marketingBriefSelection: MarketingBriefSelection;
  audienceSelection: AudienceSelection;
  angleSelection: AngleSelection;
  enumsByType: Record<string, EnumValue[]>;
  marketingBrief: MarketingBrief;
  productBrief: ProductBrief | null;
  selectedAngleIdx: number | null;
  targetDuration: number;
  generationModel: string;
  onGenerationModelChange: (m: string) => void;
  constraints: string;
  generating: boolean;
  includeProductBrief: boolean;
  includedBriefSections: MarketingBriefSection[];
  onIncludeProductBriefChange: (next: boolean) => void;
  onIncludedBriefSectionsChange: (next: MarketingBriefSection[]) => void;
  onRemoveTemplate: (adId: string) => void;
  onToggleElement: (adId: string, key: TemplateElementKey) => void;
  onSetElementOverride: (adId: string, key: TemplateElementKey, text: string) => void;
  onTemplatePatternsChange: React.Dispatch<React.SetStateAction<Record<string, TemplatePatternMap>>>;
  onTemplateFeatureSummariesChange: React.Dispatch<React.SetStateAction<Record<string, FeatureSummaryKey[]>>>;
  onTemplateSegmentsChange: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  onMarketingBriefSelectionChange: React.Dispatch<React.SetStateAction<MarketingBriefSelection>>;
  onAudienceSelectionChange: React.Dispatch<React.SetStateAction<AudienceSelection>>;
  onAngleSelectionChange: React.Dispatch<React.SetStateAction<AngleSelection>>;
  onAngleChange: (idx: number | null) => void;
  onTargetDurationChange: (n: number) => void;
  onConstraintsChange: (s: string) => void;
  // Kick off generation directly (skipping the preview dialog).
  onGenerate: () => void;
  // Open the "Preview LLM Prompt" dialog instead.
  onPreviewPrompt: () => void;
}

function TemplatePreview({
  templateAdIds, templateAds, templateElementMap, templateElementOverrides,
  templateTranscripts,
  templatePatterns, templateFeatureSummaries, templateSegments,
  marketingBriefSelection, audienceSelection, angleSelection,
  enumsByType,
  marketingBrief,
  productBrief,
  selectedAngleIdx, targetDuration, generationModel, onGenerationModelChange, constraints, generating,
  includeProductBrief, includedBriefSections,
  onIncludeProductBriefChange, onIncludedBriefSectionsChange,
  onRemoveTemplate, onToggleElement, onSetElementOverride,
  onTemplatePatternsChange, onTemplateFeatureSummariesChange, onTemplateSegmentsChange,
  onMarketingBriefSelectionChange, onAudienceSelectionChange, onAngleSelectionChange,
  onAngleChange,
  onTargetDurationChange, onConstraintsChange, onGenerate, onPreviewPrompt,
}: TemplatePreviewProps) {
  const angles = marketingBrief.creativeAngles || [];
  const selectedAngle = (selectedAngleIdx != null && angles[selectedAngleIdx]) || null;

  // UI state: which sections are in "edit" mode (checkboxes visible) vs.
  // read-only. Defaults to all read-only so the user sees a clean summary
  // of what will be sent; clicking Edit on a section reveals its
  // checkboxes/dropdowns.
  const [editingSections, setEditingSections] = useState<Set<string>>(new Set());
  const isEditing = (key: string) => editingSections.has(key);
  const toggleEditing = (key: string) => setEditingSections(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  // Both panels start expanded so what gets sent is visible at a glance;
  // the user can collapse them to focus on the templates.
  const [productBriefPanelOpen, setProductBriefPanelOpen] = useState(true);
  const [audienceAnglePanelOpen, setAudienceAnglePanelOpen] = useState(true);

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2">Template Preview</Typography>
          <Chip size="small" variant="outlined"
            label={`${templateAdIds.length} template${templateAdIds.length === 1 ? '' : 's'}`} />
        </Box>
      </Box>

      {/* Per-template accordions — each template carries its own Creative
          patterns / Features summary / Segments selections. Persuasion beats
          and the legacy element-inheritance UI are gone in this version. */}
      {templateAdIds.map(adId => {
        const ad = templateAds[adId];
        if (!ad) return null;
        const f = ad.features;
        const patternsForAd: TemplatePatternMap = templatePatterns[adId] || {};
        const summariesForAd: FeatureSummaryKey[] = templateFeatureSummaries[adId] || [];
        const segmentsForAd: string[] = templateSegments[adId] || [];

        const updatePattern = (key: TemplatePatternKey, patch: Partial<TemplatePatternEntry>) => {
          onTemplatePatternsChange(prev => {
            const cur = prev[adId] || {};
            const entry: TemplatePatternEntry = { enabled: true, value: null, ...cur[key], ...patch };
            return { ...prev, [adId]: { ...cur, [key]: entry } };
          });
        };
        const toggleSummary = (key: FeatureSummaryKey) => {
          onTemplateFeatureSummariesChange(prev => {
            const cur = prev[adId] || [];
            const next = cur.includes(key) ? cur.filter(k => k !== key) : [...cur, key];
            return { ...prev, [adId]: next };
          });
        };
        const toggleSegment = (segId: string) => {
          onTemplateSegmentsChange(prev => {
            const cur = prev[adId] || [];
            const next = cur.includes(segId) ? cur.filter(s => s !== segId) : [...cur, segId];
            return { ...prev, [adId]: next };
          });
        };

        return (
          <Accordion key={adId} defaultExpanded={templateAdIds.length <= 2} disableGutters
            sx={{ mb: 1, '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}
              sx={{
                flexDirection: 'row-reverse',
                '& .MuiAccordionSummary-expandIconWrapper': { mr: 1 },
                '& .MuiAccordionSummary-content': { ml: 0 },
              }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', pr: 1 }}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>{ad.pageName || 'Unknown'}</Typography>
                  <Typography variant="caption" color="text.secondary"
                    sx={{ display: 'block', maxWidth: 480, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    "{ad.headline || 'No headline'}"
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {ad.overallGrade != null && (
                    <Chip size="small" label={`Grade ${ad.overallGrade}`} variant="outlined" />
                  )}
                  <Button size="small" color="warning" onClick={(e) => { e.stopPropagation(); onRemoveTemplate(adId); }}>
                    Remove
                  </Button>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              {/* 1. Ad style — the editable creative-patterns section. The
                  legacy read-only "Ad style" chip block was removed because
                  it duplicated this section's read-only view. */}
              {(() => {
                const editKey = `${adId}:patterns`;
                const editing = isEditing(editKey);
                return (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Ad style
                      </Typography>
                      <Button size="small" sx={{ minWidth: 0, fontSize: '0.7rem' }}
                        onClick={() => toggleEditing(editKey)}>
                        {editing ? 'Done' : 'Edit'}
                      </Button>
                    </Box>
                    {!editing ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                        {ALL_PATTERN_KEYS.map(key => {
                          const entry = patternsForAd[key];
                          const enabled = !entry || entry.enabled !== false;
                          if (!enabled) return null;
                          const value = entry?.value ?? (f as any)?.[key];
                          if (value == null || value === '') return null;
                          return (
                            <Chip key={key} size="small" variant="outlined"
                              label={`${PATTERN_KEY_LABELS[key]}: ${formatEnumLabel(String(value))}`} />
                          );
                        })}
                        {ALL_PATTERN_KEYS.every(key => {
                          const entry = patternsForAd[key];
                          const enabled = !entry || entry.enabled !== false;
                          if (!enabled) return true;
                          const value = entry?.value ?? (f as any)?.[key];
                          return value == null || value === '';
                        }) && (
                          <Typography variant="caption" color="text.disabled">No patterns enabled.</Typography>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 1, mb: 1.5 }}>
                        {ALL_PATTERN_KEYS.map(key => {
                          const entry: TemplatePatternEntry = patternsForAd[key] || { enabled: true, value: null };
                          const enumType = PATTERN_ENUM_TYPES[key];
                          const options = (enumsByType[enumType] || []).map(e => e.enumValue);
                          const inheritedValue = (f as any)?.[key] ?? '';
                          const displayValue = entry.value ?? (typeof inheritedValue === 'string' ? inheritedValue : '');
                          return (
                            <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Checkbox size="small" sx={{ p: 0.5 }}
                                checked={entry.enabled !== false}
                                onChange={e => updatePattern(key, { enabled: e.target.checked })} />
                              <Autocomplete<string, false, false, true>
                                size="small" freeSolo fullWidth
                                disabled={entry.enabled === false}
                                options={options}
                                value={displayValue}
                                inputValue={displayValue}
                                onInputChange={(_, v) => {
                                  const next = v && v.trim() ? v : null;
                                  updatePattern(key, { value: next });
                                }}
                                renderInput={p => (
                                  <TextField {...p} label={PATTERN_KEY_LABELS[key]}
                                    sx={{ '& .MuiInputBase-input': { fontSize: '0.8rem' } }} />
                                )}
                              />
                            </Box>
                          );
                        })}
                      </Box>
                    )}
                  </>
                );
              })()}

              {/* 2-4. Hook / Core benefit / Unique angle context lines.
                  Always visible for quick reference. Editable toggles for
                  the broader summary set live in the collapsed "Features
                  summary" section below. */}
              {f?.hookSummary && (
                <Typography variant="caption" sx={{ display: 'block', mb: 0.25 }}>
                  <b>Hook:</b> {f.hookSummary}
                </Typography>
              )}
              {f?.coreBenefit && (
                <Typography variant="caption" sx={{ display: 'block', mb: 0.25 }}>
                  <b>Core benefit:</b> {f.coreBenefit}
                </Typography>
              )}
              {f?.uniqueAngle && (
                <Typography variant="caption" sx={{ display: 'block', mb: 1.5 }}>
                  <b>Unique angle:</b> {f.uniqueAngle}
                </Typography>
              )}

              {/* 5. Ad segments — rendered as a chain of detail-rich cards
                  with the role, duration, and summary all visible inline.
                  Each card shows the verbatim script of that segment on
                  hover. Cards wrap to the next row when they run out of
                  horizontal space, and the inner text width grows/shrinks
                  to try to keep neighboring cards on the same row. */}
              {Array.isArray(ad.segments) && ad.segments.length > 0 && (() => {
                const editKey = `${adId}:segments`;
                const editing = isEditing(editKey);
                const transcript = templateTranscripts[adId] || null;
                const ordered = ad.segments.slice().sort((a, b) => (a.segmentOrder ?? 0) - (b.segmentOrder ?? 0));
                const visibleChain = editing ? ordered : ordered.filter(s => segmentsForAd.includes(s.segmentId));
                return (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Ad segments (structural blueprint to mirror)
                      </Typography>
                      <Button size="small" sx={{ minWidth: 0, fontSize: '0.7rem' }}
                        onClick={() => toggleEditing(editKey)}>
                        {editing ? 'Done' : 'Edit'}
                      </Button>
                    </Box>
                    <Box sx={{
                      p: 1, bgcolor: '#fafafa', borderRadius: 1, mb: 1.5,
                      display: 'flex', flexWrap: 'wrap', alignItems: 'stretch',
                      rowGap: 1, columnGap: 0.5,
                    }}>
                      {visibleChain.length === 0 && (
                        <Typography variant="caption" color="text.disabled">No segments selected.</Typography>
                      )}
                      {visibleChain.map((seg, i) => {
                        const selected = segmentsForAd.includes(seg.segmentId);
                        const dur = seg.startTime != null && seg.endTime != null
                          ? Math.max(1, Math.round(seg.endTime - seg.startTime))
                          : null;
                        const script = scriptLinesForSegment(transcript, seg.lineStart, seg.lineEnd)
                          || seg.transcriptExcerpt
                          || null;
                        const borderColor = (seg.segmentRole && ROLE_COLORS[seg.segmentRole]) || '#757575';
                        const tooltipBody = (
                          <Box sx={{ p: 0.5, maxWidth: 400 }}>
                            <Typography variant="caption" fontWeight={700}
                              sx={{ display: 'block', mb: 0.5, color: '#fff' }}>
                              Script — {formatEnumLabel(seg.segmentRole)}{dur != null ? ` (${dur}s)` : ''}
                            </Typography>
                            {script ? (
                              <Typography variant="body2"
                                sx={{ whiteSpace: 'pre-wrap', fontStyle: 'italic', color: '#fff', lineHeight: 1.45 }}>
                                "{script}"
                              </Typography>
                            ) : (
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                {transcript ? 'No script for this segment range.' : 'Transcript not loaded yet.'}
                              </Typography>
                            )}
                          </Box>
                        );
                        return (
                          <React.Fragment key={seg.segmentId}>
                            {i > 0 && (
                              <Box sx={{ display: 'flex', alignItems: 'center', mx: 0.25 }}>
                                <Typography sx={{ color: 'text.disabled', fontSize: '1.25rem', lineHeight: 1 }}>›</Typography>
                              </Box>
                            )}
                            <Tooltip title={tooltipBody} arrow placement="top">
                              <Box
                                onClick={editing ? () => toggleSegment(seg.segmentId) : undefined}
                                sx={{
                                  flex: '1 1 220px',
                                  minWidth: 200,
                                  maxWidth: 360,
                                  p: 1,
                                  borderRadius: 1,
                                  borderLeft: `4px solid ${borderColor}`,
                                  border: '1px solid',
                                  borderColor: selected ? 'divider' : 'divider',
                                  bgcolor: selected ? '#fff' : '#f0f0f0',
                                  opacity: editing && !selected ? 0.55 : 1,
                                  cursor: editing ? 'pointer' : 'default',
                                  transition: 'opacity 120ms, background-color 120ms',
                                  '&:hover': editing ? { opacity: 0.9 } : undefined,
                                }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                                  <Typography variant="caption" fontWeight={700}>
                                    {seg.segmentOrder}.
                                  </Typography>
                                  <Chip
                                    size="small"
                                    label={formatEnumLabel(seg.segmentRole)}
                                    sx={{
                                      height: 20, fontSize: '0.7rem',
                                      bgcolor: borderColor, color: '#fff', fontWeight: 600,
                                    }}
                                  />
                                  {dur != null && (
                                    <Chip
                                      size="small" variant="outlined"
                                      label={`${dur}s`}
                                      sx={{ height: 20, fontSize: '0.7rem' }}
                                    />
                                  )}
                                </Box>
                                {seg.summary && (
                                  <Typography variant="caption" color="text.secondary"
                                    sx={{ display: 'block', whiteSpace: 'normal', lineHeight: 1.35 }}>
                                    {seg.summary}
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          </React.Fragment>
                        );
                      })}
                    </Box>
                  </>
                );
              })()}

              {/* 6. Features summary — collapsed by default. Click the
                  header to expand; inside, an Edit toggle reveals
                  checkboxes for each summary key. */}
              {(() => {
                const editKey = `${adId}:summaries`;
                const editing = isEditing(editKey);
                const panelKey = `${adId}:summariesPanel`;
                const panelOpen = isEditing(panelKey);
                const populatedKeys = FEATURE_SUMMARY_KEYS.filter(({ key }) => {
                  const value = (f as any)?.[key];
                  return value != null && String(value).trim() !== '';
                });
                const enabledKeys = populatedKeys.filter(({ key }) => summariesForAd.includes(key));
                const renderRow = (label: string, value: unknown, withCheckbox: boolean, key: FeatureSummaryKey) => (
                  <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
                    {withCheckbox && (
                      <Checkbox size="small" sx={{ p: 0.5, mt: -0.25 }}
                        checked={summariesForAd.includes(key)} onChange={() => toggleSummary(key)} />
                    )}
                    <Box sx={{ minWidth: 0, pl: withCheckbox ? 0 : 0.5 }}>
                      <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>{label}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                        {String(value)}
                      </Typography>
                    </Box>
                  </Box>
                );
                return (
                  <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0.5 }}>
                    <Box onClick={() => toggleEditing(panelKey)}
                      role="button" tabIndex={0}
                      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleEditing(panelKey); } }}
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none', px: 0.5 }}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleEditing(panelKey); }}
                        aria-label={panelOpen ? 'Collapse features summary' : 'Expand features summary'}>
                        {panelOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Features summary (context for the LLM)
                      </Typography>
                      <Box sx={{ flexGrow: 1 }} />
                      {panelOpen && (
                        <Button size="small" sx={{ minWidth: 0, fontSize: '0.7rem' }}
                          onClick={e => { e.stopPropagation(); toggleEditing(editKey); }}>
                          {editing ? 'Done' : 'Edit'}
                        </Button>
                      )}
                    </Box>
                    <Collapse in={panelOpen} timeout="auto" unmountOnExit>
                      <Box sx={{ pt: 0.5, px: 1, pb: 0.5 }}>
                        {editing
                          ? populatedKeys.map(({ key, label }) => renderRow(label, (f as any)?.[key], true, key))
                          : enabledKeys.length > 0
                            ? enabledKeys.map(({ key, label }) => renderRow(label, (f as any)?.[key], false, key))
                            : <Typography variant="caption" color="text.disabled">No feature summaries enabled.</Typography>
                        }
                      </Box>
                    </Collapse>
                  </Box>
                );
              })()}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Product brief — granular per-sub-item selection. Collapsed by
          default; expanded only when the user wants to prune what the LLM
          sees. Contents are auto-populated from the marketing brief. */}
      <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5, mb: 1.5, bgcolor: '#fafafa' }}>
        <Box onClick={() => setProductBriefPanelOpen(o => !o)}
          role="button" tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setProductBriefPanelOpen(o => !o); } }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none' }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setProductBriefPanelOpen(o => !o); }}
            aria-label={productBriefPanelOpen ? 'Collapse product brief' : 'Expand product brief'}>
            {productBriefPanelOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">Product brief</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Populated from the marketing brief. Open to customize which facts the LLM sees.
            </Typography>
          </Box>
        </Box>
        <Collapse in={productBriefPanelOpen} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 1 }}>
            <ProductBriefSelector
              brief={marketingBrief}
              selection={marketingBriefSelection}
              onChange={onMarketingBriefSelectionChange}
            />
          </Box>
        </Collapse>
      </Paper>

      {/* Audience & Creative Angle — collapsed by default. Same template
          + brief can be re-aimed at a different audience / angle to
          produce variant scripts. */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: '#fafafa' }}>
        <Box onClick={() => setAudienceAnglePanelOpen(o => !o)}
          role="button" tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setAudienceAnglePanelOpen(o => !o); } }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', userSelect: 'none' }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAudienceAnglePanelOpen(o => !o); }}
            aria-label={audienceAnglePanelOpen ? 'Collapse audience & creative angle' : 'Expand audience & creative angle'}>
            {audienceAnglePanelOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">Audience &amp; Creative angle</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Open to retarget the same template + brief at a different audience or creative angle.
            </Typography>
          </Box>
        </Box>
        <Collapse in={audienceAnglePanelOpen} timeout="auto" unmountOnExit>
          <Box sx={{ mt: 1 }}>
            <AudienceAngleSelector
              brief={marketingBrief}
              audienceSelection={audienceSelection}
              angleSelection={angleSelection}
              onAudienceChange={onAudienceSelectionChange}
              onAngleChange={onAngleSelectionChange}
            />
          </Box>
        </Collapse>
      </Paper>

      {/* Generation knobs — duration is a free integer input that defaults
          to the reference ad's total length whenever a template is added.
          Both storyboard + flat script are always produced (single Claude
          pass), so there's no output-format selector. */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 1.5 }}>
        <TextField size="small" type="number" label="Target duration (seconds)"
          value={Number.isFinite(targetDuration) ? targetDuration : ''}
          onChange={e => {
            const parsed = parseInt(e.target.value, 10);
            if (Number.isFinite(parsed)) {
              onTargetDurationChange(Math.max(5, Math.min(180, parsed)));
            }
          }}
          inputProps={{ min: 5, max: 180, step: 1 }}
          sx={{ width: 200 }}
          helperText="Defaults to the reference ad's length when a template is picked." />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Model</InputLabel>
          <Select value={generationModel} label="Model" onChange={e => onGenerationModelChange(e.target.value)}>
            {MODEL_OPTIONS.map(m => (
              <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TextField fullWidth multiline minRows={2} maxRows={4} size="small" value={constraints}
        onChange={e => onConstraintsChange(e.target.value)} sx={{ mb: 1.5 }}
        placeholder="Constraints: e.g., Don't mention competitors. Must include 30-day guarantee. Keep language casual."
        label="Constraints & Guidelines"
      />

      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button fullWidth variant="contained" disabled={generating || templateAdIds.length === 0}
          onClick={onGenerate}>
          {generating ? <><CircularProgress size={16} sx={{ mr: 1 }} /> Generating...</> : 'Generate script'}
        </Button>
        <Button fullWidth variant="outlined" disabled={generating || templateAdIds.length === 0}
          onClick={onPreviewPrompt}>
          Preview LLM Prompt
        </Button>
      </Box>
    </Paper>
  );
}

// ── Granular product-brief sub-item selector (custom mode) ──
// Renders an expandable panel per brief section. Each section shows a
// section-level checkbox (toggles every child) plus per-item checkboxes.
// Array items (differentiators, primary benefits, etc.) get one checkbox
// each; scalar fields (corePromise, mainAngle, guarantee, rating,
// reviewCount) get one checkbox each as well. Audience lives in its own
// component below.

interface ProductBriefSelectorProps {
  brief: MarketingBrief;
  selection: MarketingBriefSelection;
  onChange: React.Dispatch<React.SetStateAction<MarketingBriefSelection>>;
}

function ProductBriefSelector({ brief, selection, onChange }: ProductBriefSelectorProps) {
  // Per-section edit state. Sections render their selected items read-only
  // by default; clicking Edit on a section reveals the checkboxes.
  const [editingBriefSections, setEditingBriefSections] = useState<Set<string>>(new Set());
  const briefEditing = (k: string) => editingBriefSections.has(k);
  const toggleBriefEditing = (k: string) => setEditingBriefSections(prev => {
    const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n;
  });
  const setScalar = (section: keyof MarketingBriefSelection, key: string, value: boolean) => {
    onChange(prev => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [key]: value },
    }) as MarketingBriefSelection);
  };
  const setIndexes = (section: keyof MarketingBriefSelection, key: string, indexes: number[]) => {
    onChange(prev => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [key]: indexes },
    }) as MarketingBriefSelection);
  };
  const toggleIndex = (section: keyof MarketingBriefSelection, key: string, idx: number, current: number[]) => {
    const has = current.includes(idx);
    setIndexes(section, key, has ? current.filter(i => i !== idx) : [...current, idx].sort((a, b) => a - b));
  };

  // Render a single read-only row (used for the non-editing summary).
  const readonlyRow = (i: number, label: string, preview?: string | number | null) => (
    <Box key={i} sx={{ pl: 1, mb: 0.25 }}>
      <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>{label}</Typography>
      {preview != null && preview !== '' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
          {String(preview)}
        </Typography>
      )}
    </Box>
  );

  const sections: Array<{
    key: keyof MarketingBriefSelection;
    label: string;
    renderEditing: () => React.ReactNode;
    renderReadonly: () => React.ReactNode;
    allOn: boolean;
    anyOn: boolean;
    onSelectAll: (on: boolean) => void;
  }> = [];

  // Positioning
  {
    const pos = brief.positioning || {};
    const sel = (selection.positioning || {}) as { corePromise?: boolean; mainAngle?: boolean; differentiators?: number[] };
    const diffs = Array.isArray(pos.differentiators) ? pos.differentiators : [];
    const diffSel = sel.differentiators || [];
    const items: Array<{ on: boolean; setter: (v: boolean) => void; label: string; preview?: string | null }> = [];
    if (pos.corePromise) items.push({ on: !!sel.corePromise, setter: v => setScalar('positioning', 'corePromise', v), label: 'Core promise', preview: pos.corePromise });
    if (pos.mainAngle) items.push({ on: !!sel.mainAngle, setter: v => setScalar('positioning', 'mainAngle', v), label: 'Main angle', preview: pos.mainAngle });
    diffs.forEach((d, i) => items.push({
      on: diffSel.includes(i),
      setter: () => toggleIndex('positioning', 'differentiators', i, diffSel),
      label: `Differentiator ${i + 1}`,
      preview: d,
    }));
    const allOn = items.length > 0 && items.every(i => i.on);
    const anyOn = items.some(i => i.on);
    sections.push({
      key: 'positioning', label: 'Positioning',
      allOn, anyOn,
      onSelectAll: on => {
        setScalar('positioning', 'corePromise', on && !!pos.corePromise);
        setScalar('positioning', 'mainAngle', on && !!pos.mainAngle);
        setIndexes('positioning', 'differentiators', on ? diffs.map((_, i) => i) : []);
      },
      renderEditing: () => (
        <>
          {items.length === 0 && (
            <Typography variant="caption" color="text.disabled">No positioning items.</Typography>
          )}
          {items.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
              <Checkbox size="small" sx={{ p: 0.5, mt: -0.25 }} checked={item.on} onChange={e => item.setter(e.target.checked)} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>{item.label}</Typography>
                {item.preview && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                    {item.preview}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </>
      ),
      renderReadonly: () => {
        const onItems = items.filter(i => i.on);
        if (onItems.length === 0) return <Typography variant="caption" color="text.disabled">Nothing selected.</Typography>;
        return <>{onItems.map((item, i) => readonlyRow(i, item.label, item.preview))}</>;
      },
    });
  }

  // Benefits
  {
    const ben = brief.benefits || {};
    const sel = (selection.benefits || {}) as { primary?: number[] };
    const items = Array.isArray(ben.primary) ? ben.primary : [];
    const cur = sel.primary || [];
    const allOn = items.length > 0 && items.every((_, i) => cur.includes(i));
    const anyOn = cur.length > 0;
    sections.push({
      key: 'benefits', label: 'Benefits',
      allOn, anyOn,
      onSelectAll: on => setIndexes('benefits', 'primary', on ? items.map((_, i) => i) : []),
      renderEditing: () => (
        <>
          {items.length === 0 && (
            <Typography variant="caption" color="text.disabled">No benefits listed.</Typography>
          )}
          {items.map((b, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
              <Checkbox size="small" sx={{ p: 0.5, mt: -0.25 }}
                checked={cur.includes(i)}
                onChange={() => toggleIndex('benefits', 'primary', i, cur)} />
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{b}</Typography>
            </Box>
          ))}
        </>
      ),
      renderReadonly: () => {
        const onItems = items.filter((_, i) => cur.includes(i));
        if (onItems.length === 0) return <Typography variant="caption" color="text.disabled">Nothing selected.</Typography>;
        return <Box component="ul" sx={{ pl: 2, m: 0 }}>
          {onItems.map((b, i) => (
            <Box component="li" key={i}><Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{b}</Typography></Box>
          ))}
        </Box>;
      },
    });
  }

  // Features & Claims
  {
    const fc = brief.featuresAndClaims || {};
    const sel = (selection.featuresAndClaims || {}) as { performanceClaims?: number[]; certifications?: number[]; proprietaryTech?: number[] };
    const claims = Array.isArray(fc.performanceClaims) ? fc.performanceClaims : [];
    const certs = Array.isArray(fc.certifications) ? fc.certifications : [];
    const techs = Array.isArray(fc.proprietaryTech) ? fc.proprietaryTech : [];
    const claimSel = sel.performanceClaims || [];
    const certSel = sel.certifications || [];
    const techSel = sel.proprietaryTech || [];
    const totalItems = claims.length + certs.length + techs.length;
    const totalOn = claimSel.length + certSel.length + techSel.length;
    const allOn = totalItems > 0 && totalOn === totalItems;
    const anyOn = totalOn > 0;
    sections.push({
      key: 'featuresAndClaims', label: 'Features & claims',
      allOn, anyOn,
      onSelectAll: on => {
        setIndexes('featuresAndClaims', 'performanceClaims', on ? claims.map((_, i) => i) : []);
        setIndexes('featuresAndClaims', 'certifications', on ? certs.map((_, i) => i) : []);
        setIndexes('featuresAndClaims', 'proprietaryTech', on ? techs.map((_, i) => i) : []);
      },
      renderEditing: () => (
        <>
          {claims.length > 0 && (
            <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mt: 0.25 }}>Performance claims</Typography>
          )}
          {claims.map((c, i) => (
            <Box key={`c${i}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
              <Checkbox size="small" sx={{ p: 0.5, mt: -0.25 }}
                checked={claimSel.includes(i)}
                onChange={() => toggleIndex('featuresAndClaims', 'performanceClaims', i, claimSel)} />
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{c?.claim || `Claim ${i + 1}`}</Typography>
            </Box>
          ))}
          {certs.length > 0 && (
            <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mt: 0.5 }}>Certifications</Typography>
          )}
          {certs.map((c, i) => (
            <Box key={`ct${i}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
              <Checkbox size="small" sx={{ p: 0.5, mt: -0.25 }}
                checked={certSel.includes(i)}
                onChange={() => toggleIndex('featuresAndClaims', 'certifications', i, certSel)} />
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{c?.name || `Certification ${i + 1}`}</Typography>
            </Box>
          ))}
          {techs.length > 0 && (
            <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mt: 0.5 }}>Proprietary tech</Typography>
          )}
          {techs.map((t, i) => (
            <Box key={`t${i}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
              <Checkbox size="small" sx={{ p: 0.5, mt: -0.25 }}
                checked={techSel.includes(i)}
                onChange={() => toggleIndex('featuresAndClaims', 'proprietaryTech', i, techSel)} />
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{t?.name || `Tech ${i + 1}`}</Typography>
            </Box>
          ))}
          {totalItems === 0 && (
            <Typography variant="caption" color="text.disabled">No features or claims listed.</Typography>
          )}
        </>
      ),
      renderReadonly: () => {
        const onClaims = claims.filter((_, i) => claimSel.includes(i));
        const onCerts = certs.filter((_, i) => certSel.includes(i));
        const onTechs = techs.filter((_, i) => techSel.includes(i));
        if (onClaims.length + onCerts.length + onTechs.length === 0) {
          return <Typography variant="caption" color="text.disabled">Nothing selected.</Typography>;
        }
        return (
          <>
            {onClaims.length > 0 && (
              <>
                <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>Performance claims</Typography>
                <Box component="ul" sx={{ pl: 2, m: 0, mb: 0.5 }}>
                  {onClaims.map((c, i) => <Box component="li" key={i}><Typography variant="caption">{c?.claim || `Claim ${i + 1}`}</Typography></Box>)}
                </Box>
              </>
            )}
            {onCerts.length > 0 && (
              <>
                <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>Certifications</Typography>
                <Box component="ul" sx={{ pl: 2, m: 0, mb: 0.5 }}>
                  {onCerts.map((c, i) => <Box component="li" key={i}><Typography variant="caption">{c?.name || `Certification ${i + 1}`}</Typography></Box>)}
                </Box>
              </>
            )}
            {onTechs.length > 0 && (
              <>
                <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>Proprietary tech</Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {onTechs.map((t, i) => <Box component="li" key={i}><Typography variant="caption">{t?.name || `Tech ${i + 1}`}</Typography></Box>)}
                </Box>
              </>
            )}
          </>
        );
      },
    });
  }

  // Proof
  {
    const pr = brief.proof || {};
    const sel = (selection.proof || {}) as { rating?: boolean; reviewCount?: boolean; reviewThemes?: number[]; guarantee?: boolean };
    const themes = Array.isArray(pr.reviewThemes) ? pr.reviewThemes : [];
    const themesSel = sel.reviewThemes || [];
    const scalarItems: Array<{ on: boolean; setter: (v: boolean) => void; label: string; preview?: string | number | null }> = [];
    if (pr.rating != null) scalarItems.push({ on: !!sel.rating, setter: v => setScalar('proof', 'rating', v), label: 'Rating', preview: pr.rating });
    if (pr.reviewCount != null) scalarItems.push({ on: !!sel.reviewCount, setter: v => setScalar('proof', 'reviewCount', v), label: 'Review count', preview: pr.reviewCount });
    if (pr.guarantee) scalarItems.push({ on: !!sel.guarantee, setter: v => setScalar('proof', 'guarantee', v), label: 'Guarantee', preview: pr.guarantee });
    const totalItems = scalarItems.length + themes.length;
    const totalOn = scalarItems.filter(i => i.on).length + themesSel.length;
    const allOn = totalItems > 0 && totalOn === totalItems;
    const anyOn = totalOn > 0;
    sections.push({
      key: 'proof', label: 'Proof',
      allOn, anyOn,
      onSelectAll: on => {
        if (pr.rating != null) setScalar('proof', 'rating', on);
        if (pr.reviewCount != null) setScalar('proof', 'reviewCount', on);
        if (pr.guarantee) setScalar('proof', 'guarantee', on);
        setIndexes('proof', 'reviewThemes', on ? themes.map((_, i) => i) : []);
      },
      renderEditing: () => (
        <>
          {scalarItems.map((item, i) => (
            <Box key={`s${i}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
              <Checkbox size="small" sx={{ p: 0.5, mt: -0.25 }} checked={item.on} onChange={e => item.setter(e.target.checked)} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>{item.label}</Typography>
                {item.preview != null && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                    {String(item.preview)}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
          {themes.length > 0 && (
            <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mt: 0.5 }}>Review themes</Typography>
          )}
          {themes.map((th, i) => (
            <Box key={`th${i}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
              <Checkbox size="small" sx={{ p: 0.5, mt: -0.25 }}
                checked={themesSel.includes(i)}
                onChange={() => toggleIndex('proof', 'reviewThemes', i, themesSel)} />
              <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{th?.theme || `Theme ${i + 1}`}</Typography>
            </Box>
          ))}
          {totalItems === 0 && (
            <Typography variant="caption" color="text.disabled">No proof items listed.</Typography>
          )}
        </>
      ),
      renderReadonly: () => {
        const onScalars = scalarItems.filter(i => i.on);
        const onThemes = themes.filter((_, i) => themesSel.includes(i));
        if (onScalars.length + onThemes.length === 0) {
          return <Typography variant="caption" color="text.disabled">Nothing selected.</Typography>;
        }
        return (
          <>
            {onScalars.map((item, i) => readonlyRow(i, item.label, item.preview))}
            {onThemes.length > 0 && (
              <>
                <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>Review themes</Typography>
                <Box component="ul" sx={{ pl: 2, m: 0 }}>
                  {onThemes.map((th, i) => <Box component="li" key={i}><Typography variant="caption">{th?.theme || `Theme ${i + 1}`}</Typography></Box>)}
                </Box>
              </>
            )}
          </>
        );
      },
    });
  }

  return (
    <Box>
      {sections.map(section => {
        const editing = briefEditing(section.key);
        return (
          <Accordion key={section.key} defaultExpanded={section.key === 'positioning'} disableGutters
            sx={{ mb: 0.5, '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                {editing && (
                  <Checkbox size="small" sx={{ p: 0.5 }}
                    checked={section.allOn}
                    indeterminate={section.anyOn && !section.allOn}
                    onClick={e => e.stopPropagation()}
                    onChange={e => section.onSelectAll(e.target.checked)} />
                )}
                <Typography variant="body2" fontWeight={600}>{section.label}</Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" sx={{ minWidth: 0, fontSize: '0.7rem' }}
                  onClick={e => { e.stopPropagation(); toggleBriefEditing(section.key); }}>
                  {editing ? 'Done' : 'Edit'}
                </Button>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {editing ? section.renderEditing() : section.renderReadonly()}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}

// ── Audience & Creative Angle selector (custom mode) ──
// Two stacked sub-sections inside one panel:
//   1. Audience — sub-item checkboxes for primary / painPoints / desires /
//      objections.
//   2. Creative angle — radio-select one angle, then toggle which of its
//      sub-fields (angle, customerPain, corePromise, supportingEvidence,
//      suggestedHook, whyItWorks) to send to the LLM.

interface AudienceAngleSelectorProps {
  brief: MarketingBrief;
  audienceSelection: AudienceSelection;
  angleSelection: AngleSelection;
  onAudienceChange: React.Dispatch<React.SetStateAction<AudienceSelection>>;
  onAngleChange: React.Dispatch<React.SetStateAction<AngleSelection>>;
}

function AudienceAngleSelector({
  brief, audienceSelection, angleSelection, onAudienceChange, onAngleChange,
}: AudienceAngleSelectorProps) {
  const aud = brief.audience || {};
  const angles = Array.isArray(brief.creativeAngles) ? brief.creativeAngles : [];

  // Per-subsection edit state — defaults to read-only summaries.
  const [audEditing, setAudEditing] = useState(false);
  const [angleEditing, setAngleEditing] = useState(false);

  const toggleAudIndex = (key: 'painPoints' | 'desires' | 'objections', idx: number) => {
    onAudienceChange(prev => {
      const cur = prev[key] || [];
      const next = cur.includes(idx) ? cur.filter(i => i !== idx) : [...cur, idx].sort((a, b) => a - b);
      return { ...prev, [key]: next };
    });
  };

  const setAllAud = (on: boolean) => {
    onAudienceChange({
      primary: on && !!aud.primary,
      painPoints: on && Array.isArray(aud.painPoints) ? aud.painPoints.map((_, i) => i) : [],
      desires: on && Array.isArray(aud.desires) ? aud.desires.map((_, i) => i) : [],
      objections: on && Array.isArray(aud.objections) ? aud.objections.map((_, i) => i) : [],
    });
  };

  const audItems = [
    aud.primary ? 1 : 0,
    Array.isArray(aud.painPoints) ? aud.painPoints.length : 0,
    Array.isArray(aud.desires) ? aud.desires.length : 0,
    Array.isArray(aud.objections) ? aud.objections.length : 0,
  ].reduce((a, b) => a + b, 0);
  const audOn = [
    audienceSelection.primary ? 1 : 0,
    (audienceSelection.painPoints || []).length,
    (audienceSelection.desires || []).length,
    (audienceSelection.objections || []).length,
  ].reduce((a, b) => a + b, 0);

  const angleIdx = angleSelection.idx;
  const selectedAngle = angleIdx != null ? angles[angleIdx] : null;
  const angleFieldSet = new Set(angleSelection.fields || []);

  return (
    <Box>
      {/* Audience */}
      <Accordion defaultExpanded disableGutters
        sx={{ mb: 0.5, '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            {audEditing && (
              <Checkbox size="small" sx={{ p: 0.5 }}
                checked={audItems > 0 && audOn === audItems}
                indeterminate={audOn > 0 && audOn < audItems}
                onClick={e => e.stopPropagation()}
                onChange={e => setAllAud(e.target.checked)} />
            )}
            <Typography variant="body2" fontWeight={600}>Audience</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" sx={{ minWidth: 0, fontSize: '0.7rem' }}
              onClick={e => { e.stopPropagation(); setAudEditing(v => !v); }}>
              {audEditing ? 'Done' : 'Edit'}
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {audEditing ? (
            <>
              {aud.primary && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
                  <Checkbox size="small" sx={{ p: 0.5, mt: -0.25 }}
                    checked={!!audienceSelection.primary}
                    onChange={e => onAudienceChange(prev => ({ ...prev, primary: e.target.checked }))} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>Primary audience</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                      {aud.primary}
                    </Typography>
                  </Box>
                </Box>
              )}
              {(['painPoints', 'desires', 'objections'] as const).map(key => {
                const items = (aud[key] || []) as string[];
                if (items.length === 0) return null;
                const cur = audienceSelection[key] || [];
                const label = key === 'painPoints' ? 'Pain points'
                  : key === 'desires' ? 'Desires' : 'Objections';
                return (
                  <Box key={key} sx={{ mt: 0.5 }}>
                    <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>{label}</Typography>
                    {items.map((item, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
                        <Checkbox size="small" sx={{ p: 0.5, mt: -0.25 }}
                          checked={cur.includes(i)}
                          onChange={() => toggleAudIndex(key, i)} />
                        <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>{item}</Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })}
              {audItems === 0 && (
                <Typography variant="caption" color="text.disabled">No audience items listed.</Typography>
              )}
            </>
          ) : (
            // Read-only summary of currently-selected audience items.
            (() => {
              const onPains = (aud.painPoints || []).filter((_, i) => (audienceSelection.painPoints || []).includes(i)) as string[];
              const onDesires = (aud.desires || []).filter((_, i) => (audienceSelection.desires || []).includes(i)) as string[];
              const onObjs = (aud.objections || []).filter((_, i) => (audienceSelection.objections || []).includes(i)) as string[];
              const nothing = !audienceSelection.primary && onPains.length === 0 && onDesires.length === 0 && onObjs.length === 0;
              if (nothing) return <Typography variant="caption" color="text.disabled">Nothing selected.</Typography>;
              return (
                <>
                  {audienceSelection.primary && aud.primary && (
                    <Box sx={{ pl: 1, mb: 0.5 }}>
                      <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>Primary audience</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>{aud.primary}</Typography>
                    </Box>
                  )}
                  {([['Pain points', onPains], ['Desires', onDesires], ['Objections', onObjs]] as Array<[string, string[]]>).map(([label, items]) =>
                    items.length > 0 ? (
                      <Box key={label} sx={{ mb: 0.5 }}>
                        <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>{label}</Typography>
                        <Box component="ul" sx={{ pl: 2, m: 0 }}>
                          {items.map((item, i) => <Box component="li" key={i}><Typography variant="caption">{item}</Typography></Box>)}
                        </Box>
                      </Box>
                    ) : null
                  )}
                </>
              );
            })()
          )}
        </AccordionDetails>
      </Accordion>

      {/* Creative angle */}
      <Accordion defaultExpanded disableGutters
        sx={{ mb: 0.5, '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <Typography variant="body2" fontWeight={600}>Creative angle</Typography>
            {selectedAngle?.angle && (
              <Chip size="small" variant="outlined" label={selectedAngle.angle} />
            )}
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" sx={{ minWidth: 0, fontSize: '0.7rem' }}
              onClick={e => { e.stopPropagation(); setAngleEditing(v => !v); }}>
              {angleEditing ? 'Done' : 'Edit'}
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          {angles.length === 0 && (
            <Typography variant="caption" color="text.disabled">No creative angles in the brief.</Typography>
          )}
          {angles.length > 0 && (
            <>
              {/* Angle picker is always visible (no edit-mode gate) so the
                  user can pick / change angle directly. Per-field
                  checkboxes still hide behind the section's Edit toggle. */}
              <FormControl size="small" fullWidth sx={{ mb: 1 }}>
                <InputLabel>Selected angle</InputLabel>
                <Select value={angleIdx == null ? '' : angleIdx} label="Selected angle"
                  onChange={e => {
                    const v = e.target.value;
                    onAngleChange(prev => ({
                      ...prev,
                      idx: v === '' ? null : Number(v),
                      // Re-default fields whenever the user switches to a
                      // different angle so they don't unknowingly inherit a
                      // sub-field set from the prior pick. Picking None
                      // clears the field set entirely.
                      fields: v === '' ? [] : ANGLE_FIELD_KEYS.map(({ key }) => key),
                    }));
                  }}>
                  <MenuItem value=""><em>None — no creative angle steering</em></MenuItem>
                  {angles.map((a, i) => (
                    <MenuItem key={i} value={i}>{a.angle || `Angle ${i + 1}`}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {!selectedAngle && (
                <Typography variant="caption" color="text.disabled">
                  Pick an angle above to steer the generated script. The script will still be generated without one.
                </Typography>
              )}
              {selectedAngle && angleEditing && (
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Pick which fields of the angle to send to the LLM.
                  </Typography>
                  {ANGLE_FIELD_KEYS.map(({ key, label }) => {
                    const value = (selectedAngle as any)[key];
                    if (value == null || String(value).trim() === '') return null;
                    const checked = angleFieldSet.has(key);
                    return (
                      <Box key={key} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.25 }}>
                        <Checkbox size="small" sx={{ p: 0.5, mt: -0.25 }}
                          checked={checked}
                          onChange={() => {
                            onAngleChange(prev => {
                              const fields = new Set(prev.fields || []);
                              if (fields.has(key)) fields.delete(key); else fields.add(key);
                              return { ...prev, fields: Array.from(fields) };
                            });
                          }} />
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>{label}</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                            {value}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
              {selectedAngle && !angleEditing && (
                (() => {
                  const onFields = ANGLE_FIELD_KEYS.filter(({ key }) => {
                    if (!angleFieldSet.has(key)) return false;
                    const v = (selectedAngle as any)[key];
                    return v != null && String(v).trim() !== '';
                  });
                  if (onFields.length === 0) return (
                    <Typography variant="caption" color="text.disabled">
                      No angle fields selected. Click Edit to pick which fields to send.
                    </Typography>
                  );
                  return <>{onFields.map(({ key, label }) => (
                    <Box key={key} sx={{ pl: 1, mb: 0.5 }}>
                      <Typography variant="caption" fontWeight={600} sx={{ display: 'block' }}>{label}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', whiteSpace: 'pre-wrap' }}>
                        {(selectedAngle as any)[key]}
                      </Typography>
                    </Box>
                  ))}</>;
                })()
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

// ── Marketing Brief viewer / editor ──

interface MarketingBriefViewerProps {
  brief: MarketingBrief;
  version: number | null;
  editSource: string | null;
  saving: boolean;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onSave: (updated: MarketingBrief) => Promise<void> | void;
}

const KNOWN_BRIEF_SECTIONS: Array<{ key: string; label: string; defaultOpen?: boolean }> = [
  { key: 'positioning', label: 'Positioning', defaultOpen: true },
  { key: 'audience', label: 'Audience' },
  { key: 'benefits', label: 'Benefits' },
  { key: 'featuresAndClaims', label: 'Features & Claims' },
  { key: 'proof', label: 'Proof' },
  { key: 'creativeAngles', label: 'Creative Angles' },
  { key: 'product', label: 'Product' },
  { key: 'brand', label: 'Brand' },
];

function humanizeKey(k: string): string {
  return k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase());
}

function MarketingBriefViewer({
  brief, version, editSource, saving,
  collapsed, onToggleCollapsed,
  onSave,
}: MarketingBriefViewerProps) {
  // Local working copy. Reset whenever the upstream brief changes (e.g. after save).
  const [draft, setDraft] = useState<Record<string, unknown>>(() => ({ ...(brief as Record<string, unknown>) }));
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [sectionDraftText, setSectionDraftText] = useState<string>('');
  const [sectionDraftError, setSectionDraftError] = useState<string | null>(null);
  const [addingSection, setAddingSection] = useState(false);
  const [newSectionKey, setNewSectionKey] = useState<string>('');

  useEffect(() => {
    setDraft({ ...(brief as Record<string, unknown>) });
    setEditingSection(null);
    setSectionDraftError(null);
    setAddingSection(false);
  }, [brief]);

  const draftKeys = Object.keys(draft);
  const orderedKeys = useMemo(() => {
    const known = KNOWN_BRIEF_SECTIONS.filter(s => draftKeys.includes(s.key));
    const extras = draftKeys.filter(k => !KNOWN_BRIEF_SECTIONS.some(s => s.key === k));
    return [
      ...known,
      ...extras.map(k => ({ key: k, label: humanizeKey(k) })),
    ];
  }, [draftKeys]);

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(brief), [draft, brief]);

  const startEditSection = (key: string) => {
    setEditingSection(key);
    setSectionDraftText(JSON.stringify(draft[key] ?? null, null, 2));
    setSectionDraftError(null);
  };

  const applySectionEdit = () => {
    if (!editingSection) return;
    try {
      const parsed = JSON.parse(sectionDraftText);
      setDraft(prev => ({ ...prev, [editingSection]: parsed }));
      setEditingSection(null);
      setSectionDraftError(null);
    } catch (e: unknown) {
      setSectionDraftError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  };

  const cancelSectionEdit = () => {
    setEditingSection(null);
    setSectionDraftError(null);
  };

  const deleteSection = (key: string) => {
    setDraft(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    if (editingSection === key) setEditingSection(null);
  };

  const addSection = () => {
    const key = newSectionKey.trim();
    if (!key || Object.prototype.hasOwnProperty.call(draft, key)) {
      setAddingSection(false);
      setNewSectionKey('');
      return;
    }
    setDraft(prev => ({ ...prev, [key]: {} }));
    setAddingSection(false);
    setNewSectionKey('');
    setEditingSection(key);
    setSectionDraftText('{}');
  };

  const handleSave = () => onSave(draft as MarketingBrief);
  const handleDiscard = () => setDraft({ ...(brief as Record<string, unknown>) });

  const missingKnownKeys = KNOWN_BRIEF_SECTIONS.map(s => s.key).filter(k => !draftKeys.includes(k));

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Box onClick={onToggleCollapsed}
        role="button" tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleCollapsed(); } }}
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: collapsed ? 0 : 1, gap: 1, flexWrap: 'wrap', cursor: 'pointer', userSelect: 'none' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleCollapsed(); }}
            aria-label={collapsed ? 'Expand marketing brief' : 'Collapse marketing brief'}>
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
          <Typography variant="subtitle2">Marketing Brief</Typography>
          {version != null && <Chip label={`v${version}`} size="small" variant="outlined" color="success" />}
          {editSource && <Chip label={editSource} size="small" variant="outlined" />}
          {dirty && <Chip label="Unsaved changes" size="small" color="warning" />}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} onClick={(e) => e.stopPropagation()}>
          {dirty && (
            <>
              <Button size="small" onClick={handleDiscard} disabled={saving}>Discard</Button>
              <Button size="small" variant="contained" color="warning" onClick={handleSave} disabled={saving}>
                {saving ? <><CircularProgress size={16} sx={{ mr: 1 }} /> Saving…</> : `Save as v${(version ?? 0) + 1}`}
              </Button>
            </>
          )}
        </Box>
      </Box>
      <Collapse in={!collapsed} timeout="auto" unmountOnExit>
      {dirty && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Edits are local until you save. Saving creates a new version of the brief — earlier versions are kept in Snowflake.
        </Alert>
      )}

      {orderedKeys.map(s => {
        const known = KNOWN_BRIEF_SECTIONS.find(k => k.key === s.key);
        const value = draft[s.key];
        const isEditing = editingSection === s.key;
        return (
          <Accordion key={s.key} defaultExpanded={!!known?.defaultOpen} disableGutters elevation={0}
            sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', mb: 0.5 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 1 }}>
                <Typography variant="body2" fontWeight={600}>{s.label}</Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                  {!isEditing && (
                    <Button size="small" onClick={() => startEditSection(s.key)}>Edit</Button>
                  )}
                  <Button size="small" color="error" onClick={() => deleteSection(s.key)}>Delete</Button>
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {isEditing ? (
                <Box>
                  <TextField fullWidth multiline minRows={6} maxRows={20} variant="outlined" size="small"
                    value={sectionDraftText}
                    onChange={e => setSectionDraftText(e.target.value)}
                    sx={{ fontFamily: 'monospace', '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '12px' } }}
                  />
                  {sectionDraftError && (
                    <Alert severity="error" sx={{ mt: 1 }}>{sectionDraftError}</Alert>
                  )}
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Button size="small" variant="contained" onClick={applySectionEdit}>Apply</Button>
                    <Button size="small" onClick={cancelSectionEdit}>Cancel</Button>
                  </Box>
                </Box>
              ) : (
                <BriefValue value={value} />
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Add section footer */}
      <Box sx={{ mt: 1, mb: 1 }}>
        {addingSection ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Section</InputLabel>
              <Select value={missingKnownKeys.includes(newSectionKey) ? newSectionKey : ''}
                label="Section"
                onChange={e => setNewSectionKey(e.target.value as string)}>
                {missingKnownKeys.map(k => (
                  <MenuItem key={k} value={k}>{humanizeKey(k)}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">or</Typography>
            <TextField size="small" placeholder="customSectionKey"
              value={missingKnownKeys.includes(newSectionKey) ? '' : newSectionKey}
              onChange={e => setNewSectionKey(e.target.value)}
              sx={{ width: 200 }} />
            <Button size="small" variant="contained" onClick={addSection} disabled={!newSectionKey.trim()}>Add</Button>
            <Button size="small" onClick={() => { setAddingSection(false); setNewSectionKey(''); }}>Cancel</Button>
          </Box>
        ) : (
          <Button size="small" onClick={() => setAddingSection(true)}>+ Add section</Button>
        )}
      </Box>

      <Accordion disableGutters elevation={0}
        sx={{ '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', mt: 0.5 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="caption" color="text.secondary">Raw JSON</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ bgcolor: '#fafafa', p: 1.5, borderRadius: 1, maxHeight: 400, overflow: 'auto' }}>
            <Typography component="pre" variant="caption" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
              {JSON.stringify(draft, null, 2)}
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>
      </Collapse>
    </Paper>
  );
}

function BriefValue({ value }: { value: unknown }) {
  if (value == null || value === '') {
    return <Typography variant="caption" color="text.disabled">—</Typography>;
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{String(value)}</Typography>;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return <Typography variant="caption" color="text.disabled">—</Typography>;
    const firstScalar = value.every(v => v == null || typeof v === 'string' || typeof v === 'number');
    if (firstScalar) {
      return (
        <Box component="ul" sx={{ pl: 2.5, m: 0 }}>
          {value.map((x, i) => (
            <Typography key={i} component="li" variant="body2" sx={{ mb: 0.25 }}>{x == null ? '—' : String(x)}</Typography>
          ))}
        </Box>
      );
    }
    return (
      <Box>
        {value.map((item, i) => (
          <Paper key={i} variant="outlined" sx={{ p: 1, mb: 0.5, bgcolor: '#fafafa' }}>
            <BriefValue value={item} />
          </Paper>
        ))}
      </Box>
    );
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v != null && !(Array.isArray(v) && v.length === 0));
    if (entries.length === 0) return <Typography variant="caption" color="text.disabled">—</Typography>;
    return (
      <Box>
        {entries.map(([k, v]) => {
          const label = humanizeKey(k);
          if (typeof v === 'object' && v !== null) {
            return (
              <Box key={k} sx={{ mb: 0.75 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 0.25 }}>
                  {label}
                </Typography>
                <Box sx={{ pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
                  <BriefValue value={v} />
                </Box>
              </Box>
            );
          }
          return (
            <Typography key={k} variant="body2" sx={{ mb: 0.25 }}>
              <Box component="span" sx={{ fontWeight: 600, color: 'text.secondary' }}>{label}:</Box>{' '}
              {String(v)}
            </Typography>
          );
        })}
      </Box>
    );
  }
  return null;
}

